import { Formatters } from '../shared/lib/formatters';
import { icon } from '../shared/components/icons';
import { openExternal } from '../shared/lib/nav';
// R115 C4: the restock-order picker's catalog (pure; node lib/orderCatalog.test.mjs).
import { buildOrderCatalog, filterOrderCatalog, addToDraft, qtyInDraft, draftTotal, toOrderItems, baseName } from '../../../lib/orderCatalog.mjs';
import type { CatalogGroup, CatalogItem, DraftLine } from '../../../lib/orderCatalog.mjs';

// ── Asset Ops · Supplies & Inventory ────────────────────────────────────────
// Three surfaces under one tabbed page, all backed by /api/assetops:
//   STANDARD   — the consumables catalog (par basis + par qty per item).
//   ORDERS     — restock tickets, status pipeline, verify photos.
//   PAR/RESTOCK— per-property computed par with the basis math shown inline,
//                and the observed→shortfall auto-ticket fork.
// PRINCIPLES.md: every number states its basis. Par is never a bare count —
// it reads "2 × 4 guests = 8 rolls" so no reader needs the glossary to know
// what the 8 means.

const API = '/api/assetops';
const BASES = ['per_guest', 'per_bedroom', 'per_bathroom', 'fixed'] as const;
type ParBasis = typeof BASES[number];

interface StandardItem {
  id: string;
  name: string;
  category: string;
  unit: string;
  par_basis: ParBasis;
  par_qty: number;
  target_price: number | null;
  vendor: string;
  link: string;
  pack_qty?: number;   // units per pack as sold (12 rolls/pack); orders count packs
  track_price?: boolean;   // R115: explicit price-check opt-in/out; unset = tracked when par_qty > 0
}

interface PriceTrend { prev: number; change_pct: number | null; change_30d_pct: number | null; since: string; samples: number; }
interface PriceInfo { id: string; name: string; latest: number | null; checked_at: string | null; vs_target_pct: number | null; trend: PriceTrend | null; tracked?: boolean; last_error?: { at: string; error: string } | null; }
// R115: the last price run's summary (GET /supplies/prices last_result).
interface PriceRunResult { at: string; ran_by: string; tracked?: number; linked?: number; attempted?: number; ok?: number; failed?: number; deferred?: number; not_reached?: number; stopped?: string | null; stopped_item?: string | null; }
// R115: price tracking — the explicit flag wins; unset = tracked when the item has a par.
function priceTracked(s: { track_price?: boolean; par_qty?: number }): boolean {
  return s.track_price === true ? true : s.track_price === false ? false : Number(s.par_qty) > 0;
}

// R25 G: order lines carry the resolved spec — the property-specific variant
// (navy linen sheets at ABC) when one exists, the standard item otherwise.
interface ItemSpec { source: 'property' | 'standard'; note?: string; link?: string; vendor?: string; }
interface OrderItem { name: string; qty: number; unit_price: number; spec?: ItemSpec; }
interface PropertyItem extends StandardItem { note: string; source: 'property' | 'standard'; }
interface OrderLog { at: string; status: string; note: string; }
interface Receipt { id: string; url: string; amount: number | null; at: string; by: string; }
interface SupplyOrder {
  id: string;
  listing_id: string;
  listing_name: string;
  items: OrderItem[];
  total: number;
  actual_total?: number | null;   // sum of receipt amounts; spend of record when set
  receipts?: Receipt[];
  status: 'requested' | 'ordered' | 'delivered' | 'verified';
  source: 'manual' | 'auto-inspection' | 'inspection';
  fulfillment?: 'ground' | 'remote';
  assignee?: string;
  requested_by: string;
  requested_at: string;
  note: string;
  verify_photo: string | null;
  log: OrderLog[];
}

// Amazon's cart-add endpoint silently no-ops without an AssociateTag; any
// syntactically valid tag makes it render the "Add to Shopping Cart" confirm
// page (verified in-browser 2026-07). Not a registered Associates account —
// no commission flows anywhere.
const AMAZON_CART_TAG = 'wchops-20';
const GLOBAL_LISTING_ID = 'global';
const GLOBAL_LISTING_NAME = '🏢 Global — company-wide';

// An order's spend of record: receipt-backed actual when logged, else estimate.
function spendOf(o: SupplyOrder): number {
  return o.actual_total != null && Number.isFinite(o.actual_total) ? o.actual_total : (Number(o.total) || 0);
}

interface SmartMeta { model: SmartModel; month: string; applied_at: string; count: number; }
interface BudgetCfg { mode: 'off' | 'soft' | 'hard'; default_monthly: number; by_listing: Record<string, number>; smart?: SmartMeta | null; }
interface SmartModel { base_per_turn: number; per_bedroom: number; per_guest: number; min_monthly: number; round_to?: number; }
interface SmartRow {
  listing_id: string; name: string; bedrooms: number; max_guests: number;
  occ: number; seasonality: number; los: number; stays_12mo: number;
  est_reservations: number; per_turn: number; budget: number; flags: string[];
}
interface SmartResp {
  month: string; days_in_month: number; model: SmartModel; applied: boolean;
  basis: { airroi_year: string; listings_with_airroi: number; portfolio_avg_los: number; stays_sampled: number; median_occ: number };
  total_budget: number; rows: SmartRow[];
}
interface OverviewProp { listing_id: string; listing_name: string; spent: number; limit: number; remaining: number | null; pct: number | null; over: boolean; orders: number; open: number; }
interface Overview {
  month: string; mode: string; default_monthly: number;
  totals: { spent: number; budget: number; remaining: number; pct: number | null; over_count: number; budgeted_count: number; listings: number };
  channel_split: { ground: number; remote: number; unset: number };
  by_status: Record<string, number>;
  open_total: number; auto_tickets_open: number;
  by_property: OverviewProp[];
}

interface Listing { id: string; name: string; short_name: string | null; max_guests?: number | null; bedrooms?: number | null; bathrooms?: number | null; }

interface ParRow { name: string; par_qty: number; basis: ParBasis; }
interface ParResponse {
  listing_id: string;
  listing_name: string;
  basis_counts: { max_guests: number | null; bedrooms: number | null; bathrooms: number | null };
  par: ParRow[];
}

const ORDER_FLOW: SupplyOrder['status'][] = ['requested', 'ordered', 'delivered', 'verified'];

const BASIS_LABEL: Record<ParBasis, string> = {
  per_guest: 'per guest',
  per_bedroom: 'per bedroom',
  per_bathroom: 'per bathroom',
  fixed: 'fixed',
};

function money(n: number | null | undefined): string {
  if (!Number.isFinite(n as number)) return '—';
  return `$${(n as number).toFixed(2)}`;
}

function relTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const diff = Date.now() - t;
  if (diff < 0) return 'just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export class SuppliesPage {
  private container: HTMLElement;
  private tab: 'overview' | 'standard' | 'orders' | 'par' | 'property' = 'overview';
  // R25 G: property-variants tab state. propOverrides is the RAW override map
  // — edit inputs must read from it, not from the merged `effective` rows,
  // or saving would bake standard catalog values into the override.
  private propListingId = '';
  private propItems: PropertyItem[] = [];
  private propOverrides: Record<string, { note?: string; link?: string; vendor?: string; target_price?: number | null }> = {};
  private propLoading = false;
  private propReq = 0;   // discard out-of-order responses when switching properties

  private standard: StandardItem[] = [];
  private orders: SupplyOrder[] = [];
  private listings: Listing[] = [];
  private overview: Overview | null = null;
  private budgets: BudgetCfg | null = null;
  private budgetSearch = '';
  private budgetShowAll = false;

  // smart-budget generator state (inputs mirror here so redraws don't wipe them)
  private smartModel: SmartModel = { base_per_turn: 10, per_bedroom: 2.5, per_guest: 1, min_monthly: 40 };
  private smartModelSynced = false;   // true once seeded from the server's stored model
  private smartMonth = '';            // YYYY-MM; '' = current month
  private smartPreview: SmartResp | null = null;
  private smartBusy = false;

  // orders filters
  private orderStatusFilter: 'all' | SupplyOrder['status'] = 'all';
  private orderListingFilter = 'all';
  // R50: order ids with expanded history — class field so draw() rebuilds keep it open
  private orderLogOpen: Set<string> = new Set();

  // price intelligence (R11 #2)
  private prices: Map<string, PriceInfo> = new Map();
  private lastPriceRun: string | null = null;
  private lastPriceResult: PriceRunResult | null = null;
  private priceCap = 40;
  private priceBusy = false;

  // par/restock state
  private parListingId = '';
  private parData: ParResponse | null = null;
  private parLoading = false;

  // new-order draft items (R115 C4: lines carry client-only source/unpriced
  // flags; toOrderItems() strips them before the POST)
  private draftItems: DraftLine[] = [];
  // R115 C4: a failed catalog read is an ERROR, never an empty catalog (the
  // modal said nothing and Jesse saw no options); the inventory checklist
  // template (GET /api/ob/amenities-checklist, kind 'inventory') is fetched
  // once per page, lazily, the first time the order modal opens.
  private standardErr = false;
  private invCats: any[] | null = null;
  private invErr = false;
  private invLoad: Promise<void> | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  async render(): Promise<void> {
    this.container.innerHTML = `<div class="page-content"><div style="padding:48px;text-align:center;color:var(--text-muted);">Loading supplies…</div></div>`;
    await Promise.all([this.fetchStandard(), this.fetchOrders(), this.fetchListings(), this.fetchOverview(), this.fetchBudgets(), this.fetchPrices()]);
    this.renderShell();
  }

  private async fetchPrices(): Promise<void> {
    try {
      const j = await fetch(`${API}/supplies/prices`).then(r => r.json());
      this.prices = new Map((j.items || []).map((p: PriceInfo) => [p.id, p]));
      this.lastPriceRun = j.last_run || null;
      this.lastPriceResult = j.last_result || null;
      if (Number(j.cap) > 0) this.priceCap = Number(j.cap);
    } catch { /* keep last known prices — blanking them under a fresh timestamp would lie */ }
  }

  private async fetchOverview(): Promise<void> {
    try { this.overview = await fetch(`${API}/supplies/overview`).then(r => r.json()); }
    catch { this.overview = null; }
  }

  private async fetchBudgets(): Promise<void> {
    // R114 fix (data-safety #0): a failed read answers 503 — never treat the
    // error body as a config (spreading it posted an empty override map).
    try {
      const r = await fetch(`${API}/supplies/budgets`);
      const j = await r.json().catch(() => null);
      this.budgets = r.ok && j && typeof j.by_listing === 'object' && j.by_listing ? j : null;
    }
    catch { this.budgets = null; }
    // Seed the smart-model inputs from the last applied model, once.
    if (!this.smartModelSynced && this.budgets?.smart?.model) {
      this.smartModel = { ...this.smartModel, ...this.budgets.smart.model };
      this.smartModelSynced = true;
    }
  }

  // Local (not UTC) current month, YYYY-MM — same en-CA trick as localToday().
  private localMonth(): string {
    return new Intl.DateTimeFormat('en-CA').format(new Date()).slice(0, 7);
  }

  // ── Amazon cart deep link (R10 #3) ─────────────────────────────────────────
  // The catalog's Link column powers this: an amazon.com product URL (or a bare
  // 10-char ASIN) per item lets one click fill an Amazon cart with the order.
  private static asinFromLink(link: string | null | undefined): string | null {
    const t = (link || '').trim();
    if (!t) return null;
    if (/^[A-Z0-9]{10}$/i.test(t)) return t.toUpperCase();
    const m = t.match(/\/(?:dp|gp\/product|gp\/aw\/d)\/([A-Z0-9]{10})(?=[/?]|$)/i);
    return m ? m[1].toUpperCase() : null;
  }

  private cartMatches(o: SupplyOrder): { asin: string; qty: number }[] {
    const out: { asin: string; qty: number }[] = [];
    for (const it of o.items) {
      const std = this.standard.find(s => s.name.trim().toLowerCase() === it.name.trim().toLowerCase());
      // R25 G: property-variant link wins over the standard catalog link.
      const link = (it.spec && it.spec.link) || (std ? std.link : '');
      const asin = SuppliesPage.asinFromLink(link);
      if (asin) out.push({ asin, qty: Math.max(1, Math.round(it.qty) || 1) });
    }
    return out;
  }

  // ── fetchers ───────────────────────────────────────────────────────────────
  private async fetchStandard(): Promise<void> {
    // R115 C4: check r.ok — a 500 body (a failed read whose re-seed the R114
    // settings guard refused) used to become an empty catalog with no notice.
    try {
      const r = await fetch(`${API}/supplies/standard`);
      const j = await r.json().catch(() => null);
      if (!r.ok || !j || !Array.isArray(j.supplies)) throw new Error(`HTTP ${r.status}`);
      this.standard = j.supplies;
      this.standardErr = false;
    } catch { this.standard = []; this.standardErr = true; }
  }

  // R115 C4: the inventory checklist template's categories (kind 'inventory'),
  // once per page instance; a failure is remembered (the picker says so and
  // lists the catalog alone — it never blocks an order).
  private loadInventoryTemplate(): Promise<void> {
    if (this.invCats || this.invLoad) return this.invLoad || Promise.resolve();
    this.invLoad = (async () => {
      try {
        const r = await fetch('/api/ob/amenities-checklist');
        const j = await r.json().catch(() => null);
        if (!r.ok || !j || !Array.isArray(j.categories)) throw new Error(`HTTP ${r.status}`);
        this.invCats = j.categories.filter((c: any) => c && c.kind === 'inventory');
        this.invErr = false;
      } catch { this.invCats = null; this.invErr = true; }
      finally { this.invLoad = null; }
    })();
    return this.invLoad;
  }

  private async fetchOrders(): Promise<void> {
    try {
      const r = await fetch(`${API}/supplies/orders`);
      this.orders = (await r.json()).orders || [];
    } catch { this.orders = []; }
  }

  private async fetchListings(): Promise<void> {
    try {
      const r = await fetch('/api/listings/active');
      const data = await r.json();
      this.listings = (Array.isArray(data) ? data : []).map((l: any) => ({
        id: String(l.id),
        name: l.short_name || l.name || String(l.id),
        short_name: l.short_name || null,
        max_guests: l.max_guests, bedrooms: l.bedrooms, bathrooms: l.bathrooms,
      }));
    } catch { this.listings = []; }
  }

  private listingName(id: string): string {
    if (String(id) === GLOBAL_LISTING_ID) return GLOBAL_LISTING_NAME;
    const l = this.listings.find(x => x.id === String(id));
    return l ? l.name : id;
  }

  // ── shell ────────────────────────────────────────────────────────────────
  private renderShell(): void {
    this.container.innerHTML = `
      <style>
        .sup-wrap { }
        .sup-head { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; flex-wrap:wrap; margin-bottom:20px; }
        .sup-title { font-size:19px; font-weight:600; letter-spacing:-.01em; color:var(--text); margin:0; }
        .sup-sub { font-size:13px; color:var(--text-muted); margin:6px 0 0; line-height:1.5; }
        /* R115 mobile S1: the five tabs are a single-line scroller — a nowrap
           row with no overflow widened the whole document to 459px at 390. */
        .sup-tabs { display:flex; gap:4px; border-bottom:1px solid var(--border); margin-bottom:20px; overflow-x:auto; -webkit-overflow-scrolling:touch; scrollbar-width:none; }
        .sup-tabs::-webkit-scrollbar { display:none; }
        .sup-tab { flex:none; white-space:nowrap; padding:10px 16px; background:none; border:none; border-bottom:2px solid transparent; font-size:13px; font-weight:600; color:var(--text-muted); cursor:pointer; transition:color .15s ease, border-color .15s ease; }
        .sup-tab.active { color:var(--text); border-bottom-color:var(--text); }
        .sup-tab:hover:not(.active) { color:var(--text); }
        .sup-card { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); overflow:hidden; }
        .sup-card + .sup-card { margin-top:20px; }
        .sup-card-head { padding:16px 18px; border-bottom:1px solid var(--border); display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; }
        .sup-card-title { font-size:14px; font-weight:600; letter-spacing:-.005em; color:var(--text); }
        .sup-cap { font-size:12px; color:var(--text-muted); padding:12px 18px; background:var(--bg-subtle); border-bottom:1px solid var(--border); line-height:1.6; }
        table.sup-tbl { width:100%; border-collapse:collapse; table-layout:fixed; font-size:12.5px; }
        .sup-tbl th { text-align:left; font-size:10.5px; text-transform:uppercase; letter-spacing:.06em; color:var(--text-muted); font-weight:600; padding:10px 14px; border-bottom:1px solid var(--border); background:transparent; }
        .sup-tbl td { padding:10px 14px; border-bottom:1px solid var(--border-soft); vertical-align:middle; color:var(--text); }
        .sup-tbl tbody tr { transition:background .12s ease; }
        .sup-tbl tbody tr:hover { background:var(--bg-subtle); }
        .sup-tbl tr:last-child td { border-bottom:none; }
        .sup-tbl input, .sup-tbl select { width:100%; box-sizing:border-box; border:1px solid var(--border); border-radius:var(--radius-sm); padding:6px 9px; font-size:12.5px; font-family:inherit; background:var(--card); color:var(--text); transition:border-color .15s ease, box-shadow .15s ease; }
        .sup-tbl input:focus, .sup-tbl select:focus { outline:none; border-color:var(--text); box-shadow:0 0 0 3px var(--bg-subtle); }
        .num { font-variant-numeric:tabular-nums; }
        .sup-pill { display:inline-flex; align-items:center; padding:3px 11px; border-radius:var(--radius-pill); font-size:11px; font-weight:600; letter-spacing:.01em; cursor:pointer; border:1px solid transparent; transition:filter .15s ease; }
        .sup-pill:hover { filter:brightness(.97); }
        .sup-pill.requested { background:#f1f5f9; color:#475569; }
        .sup-pill.ordered { background:#fffbeb; color:#92400e; }
        .sup-pill.delivered { background:#eff6ff; color:#1e40af; }
        .sup-pill.verified { background:#ecfdf5; color:#065f46; }
        .sup-pill.locked { cursor:default; opacity:.95; }
        .sup-pill.locked:hover { filter:none; }
        .sup-chip { display:inline-flex; align-items:center; padding:2px 8px; border-radius:var(--radius-pill); font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; }
        .sup-chip.auto { background:#ede9fe; color:#5b21b6; }
        .sup-chip.manual { background:#f1f5f9; color:#64748b; }
        .sup-icon-btn { background:none; border:none; cursor:pointer; color:var(--text-faint); font-size:14px; padding:4px 7px; border-radius:var(--radius-sm); transition:background .15s ease, color .15s ease; }
        .sup-icon-btn:hover { background:var(--danger-soft); color:var(--danger); }
        .sup-price-meta { font-size:11px; color:var(--text-faint); }
        /* R115: the 11-column editable catalog keeps its widths on a phone and
           scrolls inside the card (the page itself never scrolls sideways) */
        .sup-std-scroll { overflow-x:auto; -webkit-overflow-scrolling:touch; }
        @media (max-width:900px) { .sup-std-scroll > .sup-tbl { min-width:1240px; } .sup-std-scroll > .sup-tbl th { white-space:normal; } }
        .sup-price-stop { color:var(--danger-text, var(--danger)); font-weight:600; }
        .sup-price-err { display:inline-flex; align-items:center; justify-content:center; width:16px; height:16px; border-radius:50%; background:var(--danger-soft); color:var(--danger); font-size:10px; font-weight:700; cursor:help; }
        .sup-tbl .sup-track { display:flex; align-items:center; gap:5px; min-height:36px; font-size:11px; color:var(--text-muted); cursor:pointer; }
        .sup-tbl .sup-track input { width:16px; height:16px; margin:0; padding:0; accent-color:var(--accent); }
        .sup-link-add { font-size:13px; font-weight:600; color:var(--text); background:none; border:1px dashed var(--border); border-radius:var(--radius-pill); padding:8px 16px; cursor:pointer; transition:background .15s ease, border-color .15s ease; }
        .sup-link-add:hover { background:var(--bg-subtle); border-color:var(--text-muted); }
        .sup-empty { padding:40px 32px; text-align:center; color:var(--text-faint); font-size:13px; line-height:1.6; }
        .sup-order { padding:16px 18px; border-bottom:1px solid var(--border-soft); transition:background .12s ease; }
        .sup-order:hover { background:var(--bg-subtle); }
        .sup-order:last-child { border-bottom:none; }
        .sup-order-top { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
        .sup-order-name { font-size:14px; font-weight:600; color:var(--text); }
        .sup-order-items { font-size:12px; color:var(--text-muted); margin-top:5px; line-height:1.6; }
        .sup-order-foot { display:flex; align-items:center; gap:12px; flex-wrap:wrap; margin-top:10px; font-size:11px; color:var(--text-faint); }
        .sup-stat-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:14px; }
        .sup-stat { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); padding:16px 18px; }
        .sup-stat .lbl { font-size:10.5px; text-transform:uppercase; letter-spacing:.06em; color:var(--text-muted); font-weight:600; margin-bottom:6px; }
        .sup-stat .val { font-size:24px; font-weight:600; letter-spacing:-.02em; color:var(--text); font-variant-numeric:tabular-nums; }
        .sup-stat .sub { font-size:11px; color:var(--text-faint); margin-top:4px; }
        .sup-filter { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
        .sup-filter select { border:1px solid var(--border); border-radius:var(--radius-pill); padding:7px 12px; font-size:12px; font-family:inherit; background:var(--card); color:var(--text); cursor:pointer; transition:border-color .15s ease; }
        .sup-filter select:hover { border-color:var(--text-muted); }
        .sup-filter select:focus { outline:none; border-color:var(--text); }
        .sup-math { font-variant-numeric:tabular-nums; color:var(--text-muted); font-size:12px; }
        .sup-par-num { font-weight:600; color:var(--text); }
        .sup-obs-in { width:72px; box-sizing:border-box; border:1px solid var(--border); border-radius:var(--radius-sm); padding:6px 9px; font-size:12.5px; font-family:inherit; text-align:right; background:var(--card); color:var(--text); transition:border-color .15s ease, box-shadow .15s ease; }
        .sup-obs-in:focus { outline:none; border-color:var(--text); box-shadow:0 0 0 3px var(--bg-subtle); }
        .sup-shortfall { font-size:12px; font-weight:600; font-variant-numeric:tabular-nums; }
        .sup-shortfall.short { color:var(--danger); }
        .sup-shortfall.ok { color:var(--success); }
        .sup-thumb { width:40px; height:40px; border-radius:var(--radius-sm); object-fit:cover; border:1px solid var(--border); cursor:pointer; transition:opacity .15s ease; }
        .sup-thumb:hover { opacity:.85; }
        .sup-note { font-size:13px; color:var(--text); margin-bottom:8px; }
        .sup-toast { position:fixed; bottom:24px; left:50%; transform:translateX(-50%); background:var(--text); color:#fff; padding:11px 20px; border-radius:var(--radius-pill); font-size:13px; font-weight:500; z-index:3000; box-shadow:0 8px 24px rgba(0,0,0,.18); }
        /* R115 mobile S2: the Orders head — "+ New order" leads, full width on a phone */
        @media (max-width:640px) {
          .sup-ord-tools { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:8px; width:100%; }
          .sup-ord-tools select { width:100%; min-width:0; }
          .sup-ord-tools #sup-new-order { grid-column:1 / -1; order:-1; justify-content:center; min-height:44px; font-size:13px; }
          .sup-card-head { padding:14px; }
          .sup-order { padding:14px; }
        }
        /* R115 mobile S4 + C4: the "New restock order" sheet — a flex column
           (scrolling body, sticky footer with the total + Create order), a
           bottom sheet on phones (main.css .m-sheet-wrap / .m-sheet), an item
           row that is a grid (no fixed widths: "+ Add" can never fall off the
           right edge), and the catalog listed on screen (search + tap to add). */
        .sup-no-wrap { position:fixed; inset:0; background:rgba(15,23,42,.45); z-index:2000; display:flex; align-items:center; justify-content:center; padding:16px; }
        .sup-no-card { background:var(--card); border-radius:var(--radius-lg); width:100%; max-width:600px; max-height:90vh; max-height:90dvh; display:flex; flex-direction:column; box-shadow:var(--shadow-pop); overflow:hidden; }
        .sup-no-head { flex:none; display:flex; align-items:center; justify-content:space-between; gap:10px; padding:10px 10px 10px 20px; border-bottom:1px solid var(--border); }
        .sup-no-head strong { font-size:16px; color:var(--text); }
        .sup-no-x { width:40px; height:40px; flex:none; display:inline-flex; align-items:center; justify-content:center; border:none; background:none; border-radius:var(--radius-pill); font-size:18px; color:var(--text-faint); cursor:pointer; }
        .sup-no-x:hover { background:var(--bg-subtle); color:var(--text); }
        .sup-no-body { flex:1 1 auto; overflow-y:auto; overscroll-behavior:contain; -webkit-overflow-scrolling:touch; padding:16px 20px; }
        .sup-no-foot { flex:none; border-top:1px solid var(--border); padding:12px 20px; background:var(--card); padding-bottom:max(12px, env(safe-area-inset-bottom, 0px)); }
        .sup-no-foot-row { display:flex; align-items:center; gap:12px; }
        .sup-no-sum { font-size:12.5px; color:var(--text-muted); white-space:nowrap; }
        .sup-no-sum b { color:var(--text); font-variant-numeric:tabular-nums; }
        .sup-no-foot #sup-no-submit { flex:1; justify-content:center; min-height:44px; }
        .sup-no-lab { display:block; font-size:12px; font-weight:600; color:var(--text-muted); margin-bottom:4px; }
        .sup-no-in { width:100%; min-width:0; box-sizing:border-box; border:1px solid var(--border); border-radius:var(--radius-sm); padding:8px 10px; font-size:14px; font-family:inherit; background:var(--card); color:var(--text); }
        .sup-no-in:focus { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }
        .sup-no-two { display:flex; flex-wrap:wrap; gap:12px; margin-bottom:16px; }
        .sup-no-two > div { flex:1 1 200px; min-width:0; }
        .sup-no-row { display:grid; grid-template-columns:minmax(0, 1fr) 64px 92px auto; gap:8px; margin-bottom:10px; align-items:stretch; }
        .sup-no-row .sup-no-in { text-align:left; }
        #sup-no-qty, #sup-no-price { text-align:right; }
        #sup-no-add { justify-content:center; white-space:nowrap; }
        .sup-no-hint { font-size:11.5px; color:var(--text-muted); margin:-4px 0 10px; }
        .sup-draft-none { font-size:12px; color:var(--text-faint); padding:6px 0; }
        .sup-draft td { vertical-align:middle; }
        .sup-draft .sup-noprice { font-size:11px; color:var(--text-faint); font-weight:500; }
        .sup-draft .sup-save-cat { display:inline-flex; margin-top:3px; font-size:11px; font-weight:600; color:var(--accent); background:none; border:none; padding:0; cursor:pointer; }
        .sup-draft .sup-save-cat:disabled { color:var(--text-faint); cursor:default; }
        .sup-cat { margin:14px 0 12px; border:1px solid var(--border); border-radius:var(--radius); overflow:hidden; }
        .sup-cat-h { display:flex; flex-wrap:wrap; align-items:baseline; justify-content:space-between; gap:2px 10px; padding:10px 12px; background:var(--bg-subtle); border-bottom:1px solid var(--border); font-size:12.5px; font-weight:700; color:var(--text); }
        .sup-cat-h b { white-space:nowrap; }
        .sup-cat-h span { font-size:11px; font-weight:500; color:var(--text-muted); }
        .sup-cat-sec { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:var(--text-muted); padding:12px 12px 4px; }
        .sup-cat-sec.inv { border-top:1px solid var(--border); margin-top:4px; }
        .sup-cat-group > summary { list-style:none; cursor:pointer; display:flex; align-items:center; gap:8px; min-height:40px; padding:0 12px; font-size:12.5px; font-weight:600; color:var(--text); border-top:1px solid var(--border-soft); }
        .sup-cat-group > summary::-webkit-details-marker { display:none; }
        .sup-cat-group > summary::before { content:'▸'; font-size:10px; color:var(--text-faint); transition:transform .15s ease; }
        .sup-cat-group[open] > summary::before { transform:rotate(90deg); }
        .sup-cat-group > summary .n { margin-left:auto; font-size:11px; font-weight:600; color:var(--text-faint); }
        .sup-cat-lbl { font-size:11px; font-weight:600; color:var(--text-faint); padding:8px 12px 2px; }
        .sup-cat-row { display:flex; align-items:center; justify-content:space-between; gap:10px; padding:8px 12px; border-top:1px solid var(--border-soft); }
        .sup-cat-row.in { background:var(--accent-soft); }
        .sup-cat-main { min-width:0; flex:1; }
        .sup-cat-name { font-size:13.5px; font-weight:500; color:var(--text); overflow-wrap:anywhere; }
        .sup-cat-sub { font-size:11.5px; color:var(--text-muted); margin-top:2px; }
        .sup-cat-var { font-size:10.5px; font-weight:700; color:var(--accent); }
        .sup-qty { display:flex; align-items:center; gap:6px; flex:none; }
        .sup-qbtn { width:36px; height:36px; border-radius:var(--radius-sm); border:1px solid var(--border); background:var(--card); font-size:18px; font-weight:600; line-height:1; cursor:pointer; color:var(--text); font-family:inherit; }
        .sup-qbtn:hover:not(:disabled) { border-color:var(--accent); color:var(--accent); }
        .sup-qbtn:disabled { opacity:.35; cursor:default; }
        .sup-qval { min-width:20px; text-align:center; font-variant-numeric:tabular-nums; font-weight:600; font-size:14px; }
        .sup-cat-msg { font-size:12px; color:var(--text-muted); padding:10px 12px; }
        .sup-cat-msg.err { color:var(--danger-text); background:var(--danger-soft); }
        .sup-cat-custom { display:flex; width:100%; justify-content:center; border-top:1px solid var(--border); border-radius:0; min-height:44px; }
        @media (max-width:640px) {
          .sup-no-head { padding:6px 6px 6px 16px; }
          .sup-no-body { padding:14px 16px; }
          .sup-no-foot { padding-left:16px; padding-right:16px; }
        }
        @media (max-width:480px) {
          .sup-no-row { grid-template-columns:72px minmax(0, 1fr) auto; }
          .sup-no-row #sup-no-item { grid-column:1 / -1; }
          #sup-no-add { min-width:88px; }
        }
      </style>
      <div class="page-content sup-wrap">
        <div class="dash-head">
          <h1>Supplies &amp; Inventory</h1>
          <p>Standard consumables, restock orders, and per-property par with the basis math shown.</p>
        </div>
        <div class="sup-tabs">
          <button class="sup-tab ${this.tab === 'overview' ? 'active' : ''}" data-tab="overview">Overview &amp; Budget</button>
          <button class="sup-tab ${this.tab === 'standard' ? 'active' : ''}" data-tab="standard">Standard Items</button>
          <button class="sup-tab ${this.tab === 'orders' ? 'active' : ''}" data-tab="orders">Orders</button>
          <button class="sup-tab ${this.tab === 'par' ? 'active' : ''}" data-tab="par">Par / Restock</button>
          <button class="sup-tab ${this.tab === 'property' ? 'active' : ''}" data-tab="property">Property Items</button>
        </div>
        <div id="sup-body"></div>
      </div>
    `;

    // R115 mobile S1: keep the active tab visible inside the tab scroller.
    const tabsEl = this.container.querySelector<HTMLElement>('.sup-tabs');
    const activeTab = tabsEl?.querySelector<HTMLElement>('.sup-tab.active');
    if (tabsEl && activeTab && tabsEl.scrollWidth > tabsEl.clientWidth) tabsEl.scrollLeft = Math.max(0, activeTab.getBoundingClientRect().left - tabsEl.getBoundingClientRect().left + tabsEl.scrollLeft - 16);

    this.container.querySelectorAll('.sup-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        this.tab = (btn as HTMLElement).dataset.tab as typeof this.tab;
        this.renderShell();
        // Returning to Property Items re-pulls catalog + overrides — another
        // tab (or user) may have changed the catalog since last view.
        if (this.tab === 'property' && this.propListingId) void this.loadPropertyItems();
      });
    });

    this.renderBody();
  }

  private renderBody(): void {
    const body = this.container.querySelector('#sup-body') as HTMLElement;
    if (!body) return;
    // If the user is mid-typing a receipt amount, an async redraw (another
    // order's action finishing) must not eat their keystrokes — snapshot the
    // focused input and restore value + caret after the rebuild.
    const act = document.activeElement as HTMLInputElement | null;
    const rcptFocus = (act && act.dataset && act.dataset.role === 'rcpt-amount')
      ? { rcpt: act.dataset.rcpt!, value: act.value, caret: act.selectionStart ?? act.value.length }
      : null;
    if (this.tab === 'overview') this.renderOverview(body);
    else if (this.tab === 'standard') this.renderStandard(body);
    else if (this.tab === 'orders') this.renderOrders(body);
    else if (this.tab === 'property') this.renderPropertyItems(body);
    else this.renderPar(body);
    if (rcptFocus) {
      const again = body.querySelector<HTMLInputElement>(`[data-role="rcpt-amount"][data-rcpt="${rcptFocus.rcpt}"]`);
      if (again) {
        again.value = rcptFocus.value;
        again.focus();
        try { again.setSelectionRange(rcptFocus.caret, rcptFocus.caret); } catch { /* number inputs may refuse */ }
      }
    }
  }

  private toast(msg: string): void {
    const el = document.createElement('div');
    el.className = 'sup-toast';
    el.textContent = msg;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 2200);
  }

  // ════════════════════════════════════════════════════════════════════════
  // TAB 0 — OVERVIEW & BUDGET (the supply cockpit)
  // ════════════════════════════════════════════════════════════════════════
  private renderOverview(body: HTMLElement): void {
    const ov = this.overview;
    const cfg: BudgetCfg = this.budgets || { mode: 'soft', default_monthly: 0, by_listing: {} };
    if (!ov) { body.innerHTML = `<div class="sup-empty">Supply overview unavailable — GET /api/assetops/supplies/overview failed.</div>`; return; }

    const t = ov.totals;
    const monthLabel = (() => { const [y, m] = ov.month.split('-'); return `${['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m)]} ${y}`; })();
    const modeBadge = cfg.mode === 'hard'
      ? `<span class="pill pill-red">Hard block</span>`
      : cfg.mode === 'soft' ? `<span class="pill pill-amber">Soft warn</span>` : `<span class="pill pill-gray">Off</span>`;

    // channel split bar
    const ch = ov.channel_split;
    const chTotal = ch.ground + ch.remote + ch.unset || 1;
    const pctG = Math.round((ch.ground / chTotal) * 100), pctR = Math.round((ch.remote / chTotal) * 100), pctU = 100 - pctG - pctR;

    // per-property rows (relevant by default; search or show-all expands)
    let rows = ov.by_property;
    const q = this.budgetSearch.trim().toLowerCase();
    if (q) rows = rows.filter(r => r.listing_name.toLowerCase().includes(q));
    else if (!this.budgetShowAll) rows = rows.filter(r => r.spent > 0 || r.over || cfg.by_listing[r.listing_id] != null);

    const bar = (pct: number | null, over: boolean) => {
      if (pct == null) return `<div class="bgt-bar"><div class="bgt-fill none" style="width:0%"></div></div>`;
      const w = Math.min(100, pct);
      const cls = over ? 'over' : pct > 80 ? 'warn' : 'ok';
      return `<div class="bgt-bar"><div class="bgt-fill ${cls}" style="width:${w}%"></div></div>`;
    };

    body.innerHTML = `
      <style>
        .bgt-config { display:flex; align-items:center; gap:14px; flex-wrap:wrap; }
        .bgt-config label { font-size:12px; font-weight:600; color:var(--text-muted); }
        .bgt-config select, .bgt-config input { border:1px solid var(--border); border-radius:var(--radius-sm); padding:7px 10px; font-size:13px; font-family:inherit; background:var(--card); color:var(--text); }
        .bgt-bar { width:100%; height:7px; background:var(--bg-subtle); border-radius:5px; overflow:hidden; }
        .bgt-fill { height:100%; border-radius:5px; }
        .bgt-fill.ok { background:#16a34a; } .bgt-fill.warn { background:#d97706; } .bgt-fill.over { background:#dc2626; } .bgt-fill.none { background:transparent; }
        .bgt-in { width:90px; box-sizing:border-box; border:1px solid var(--border); border-radius:var(--radius-sm); padding:6px 9px; font-size:12.5px; text-align:right; font-family:inherit; }
        .ch-bar { display:flex; height:22px; border-radius:6px; overflow:hidden; border:1px solid var(--border); }
        .ch-seg { display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; color:#fff; }
        .ch-g { background:#0891b2; } .ch-r { background:#3b82f6; } .ch-u { background:#cbd5e1; color:#475569; }
        .ch-legend { display:flex; gap:16px; margin-top:8px; font-size:11.5px; color:var(--text-muted); }
        .ch-dot { display:inline-block; width:9px; height:9px; border-radius:2px; margin-right:5px; vertical-align:middle; }
        .bgt-over-row td { background:#fef2f2; }
      </style>

      <div class="kpi-grid cols-3" style="margin-bottom:16px;">
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">Spend this month</span><span class="kpi-card-ic">${icon('wallet', 17)}</span></div>
          <div class="kpi-card-value">${money(t.spent)}</div>
          <div class="kpi-card-cap">${monthLabel} · all order statuses · receipt actuals where logged, else estimates</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">Monthly budget</span><span class="kpi-card-ic">${icon('target', 17)}</span></div>
          <div class="kpi-card-value">${t.budget > 0 ? money(t.budget) : '—'}</div>
          <div class="kpi-card-cap">${t.budgeted_count} of ${t.listings} properties budgeted</div>
        </div>
        <div class="kpi-card ${t.budget > 0 && t.remaining < 0 ? 'tone-bad' : t.budget > 0 ? 'tone-good' : ''}">
          <div class="kpi-card-top"><span class="kpi-card-label">Remaining</span><span class="kpi-card-ic">${icon('trending', 17)}</span></div>
          <div class="kpi-card-value">${t.budget > 0 ? money(t.remaining) : '—'}</div>
          <div class="kpi-card-cap">${t.pct != null ? `${t.pct}% of budget used` : 'set a budget below'}</div>
        </div>
        <div class="kpi-card ${t.over_count > 0 ? 'tone-bad' : ''}">
          <div class="kpi-card-top"><span class="kpi-card-label">Over budget</span><span class="kpi-card-ic">${icon('flag', 17)}</span></div>
          <div class="kpi-card-value">${t.over_count}</div>
          <div class="kpi-card-cap">properties past their cap</div>
        </div>
        <div class="kpi-card ${ov.open_total > 0 ? 'tone-bad' : ''}">
          <div class="kpi-card-top"><span class="kpi-card-label">Open orders</span><span class="kpi-card-ic">${icon('package', 17)}</span></div>
          <div class="kpi-card-value">${ov.open_total}</div>
          <div class="kpi-card-cap">requested or ordered</div>
        </div>
        <div class="kpi-card ${ov.auto_tickets_open > 0 ? 'tone-bad' : ''}">
          <div class="kpi-card-top"><span class="kpi-card-label">Auto-restocks</span><span class="kpi-card-ic">${icon('receipt', 17)}</span></div>
          <div class="kpi-card-value">${ov.auto_tickets_open}</div>
          <div class="kpi-card-cap">open tickets from inspections</div>
        </div>
      </div>

      <div class="sup-card">
        <div class="sup-card-head">
          <span class="sup-card-title">Budget policy ${modeBadge}</span>
        </div>
        <div class="sup-cap">
          Set a monthly supply budget per property. <strong>Soft</strong> warns when an order goes over; <strong>Hard</strong> blocks it (the requester can still override with a reason). Auto-restocks from inspections are never blocked by the budget — but a staged item short right after a clean is logged as a <strong>cleaning miss</strong> for the cleaner, not ordered (R104). Properties without an explicit budget use the default.
        </div>
        <div style="padding:16px 18px;">
          <div class="bgt-config">
            <div><label>Enforcement</label><br>
              <select id="bgt-mode">
                <option value="off" ${cfg.mode === 'off' ? 'selected' : ''}>Off — no limit</option>
                <option value="soft" ${cfg.mode === 'soft' ? 'selected' : ''}>Soft — warn over budget</option>
                <option value="hard" ${cfg.mode === 'hard' ? 'selected' : ''}>Hard — block over budget</option>
              </select>
            </div>
            <div><label>Default monthly budget / property</label><br>
              <span style="font-size:13px;color:var(--text-muted);">$</span>
              <input id="bgt-default" type="number" min="0" step="10" value="${cfg.default_monthly || ''}" placeholder="0" style="width:110px;">
            </div>
            <button class="btn btn-primary btn-sm" id="bgt-save-policy" style="align-self:flex-end;">Save policy</button>
          </div>
        </div>
      </div>

      ${this.smartCardHtml(cfg)}

      <div class="sup-card">
        <div class="sup-card-head">
          <span class="sup-card-title">Fulfillment channel · ${monthLabel}</span>
        </div>
        <div style="padding:16px 18px;">
          ${(ch.ground + ch.remote + ch.unset) > 0 ? `
          <div class="ch-bar">
            ${pctG > 0 ? `<div class="ch-seg ch-g" style="width:${pctG}%">${pctG > 8 ? 'Ground' : ''}</div>` : ''}
            ${pctR > 0 ? `<div class="ch-seg ch-r" style="width:${pctR}%">${pctR > 8 ? 'Remote' : ''}</div>` : ''}
            ${pctU > 0 ? `<div class="ch-seg ch-u" style="width:${pctU}%">${pctU > 8 ? 'Unset' : ''}</div>` : ''}
          </div>
          <div class="ch-legend">
            <span><span class="ch-dot ch-g"></span>Ground (bought locally) · ${money(ch.ground)}</span>
            <span><span class="ch-dot ch-r"></span>Remote (ordered online) · ${money(ch.remote)}</span>
            ${ch.unset > 0 ? `<span><span class="ch-dot ch-u"></span>Unset · ${money(ch.unset)}</span>` : ''}
          </div>` : `<div style="font-size:13px;color:var(--text-faint);">No spend logged this month yet.</div>`}
        </div>
      </div>

      <div class="sup-card">
        <div class="sup-card-head">
          <span class="sup-card-title">Per-property budget &amp; spend · ${monthLabel} <span style="font-weight:400;font-size:11px;color:var(--text-muted);">spent = receipt actuals where logged, else estimates</span></span>
          <div class="sup-filter">
            <input id="bgt-search" placeholder="Search property…" value="${Formatters.escapeHtml(this.budgetSearch)}" style="border:1px solid var(--border);border-radius:var(--radius-pill);padding:7px 12px;font-size:12px;font-family:inherit;">
            <button class="btn btn-secondary btn-sm" id="bgt-toggle-all">${this.budgetShowAll ? 'Show active only' : `Show all ${t.listings}`}</button>
            <button class="btn btn-primary btn-sm" id="bgt-save-props" ${this.budgets ? '' : 'disabled'}>Save budgets</button>
          </div>
        </div>
        ${this.budgets ? '' : `<div role="alert" style="padding:10px 18px; font-size:12.5px; color:var(--danger-text); border-bottom:1px solid var(--border-soft);">Couldn't load the saved budgets just now — nothing was changed. Saving is off until you reload the page.</div>`}
        <table class="sup-tbl">
          <colgroup><col style="width:30%"><col style="width:14%"><col style="width:13%"><col style="width:25%"><col style="width:18%"></colgroup>
          <thead><tr><th>Property</th><th style="text-align:right;">Budget</th><th style="text-align:right;">Spent</th><th>Used</th><th style="text-align:right;">Remaining</th></tr></thead>
          <tbody>
            ${rows.length ? rows.map(r => {
              const override = cfg.by_listing[r.listing_id];
              return `<tr class="${r.over ? 'bgt-over-row' : ''}">
                <td>${Formatters.escapeHtml(r.listing_name)}${r.open > 0 ? ` <span class="sup-chip manual" style="text-transform:none;">${r.open} open</span>` : ''}</td>
                <td style="text-align:right;"><input class="bgt-in" type="number" min="0" step="10" data-listing="${r.listing_id}" value="${override != null ? override : ''}" placeholder="${r.listing_id === GLOBAL_LISTING_ID ? 'no default' : (cfg.default_monthly || 0)}" ${r.listing_id === GLOBAL_LISTING_ID ? 'title="Global has no per-property default — only an explicit budget applies"' : ''}></td>
                <td class="num" style="text-align:right;font-weight:600;">${money(r.spent)}</td>
                <td>${bar(r.pct, r.over)}<div style="font-size:10.5px;color:var(--text-faint);margin-top:3px;">${r.pct != null ? `${r.pct}%${r.over ? ' · OVER' : ''}` : 'no budget'}</div></td>
                <td class="num" style="text-align:right;color:${r.over ? 'var(--danger)' : 'var(--text)'};">${r.remaining != null ? money(r.remaining) : '—'}</td>
              </tr>`;
            }).join('') : `<tr><td colspan="5" class="sup-empty">No ${q ? 'matching' : 'active'} properties. ${!q && !this.budgetShowAll ? 'Click “Show all” to set budgets for every property.' : ''}</td></tr>`}
          </tbody>
        </table>
      </div>
    `;

    // ── binds ──
    (body.querySelector('#bgt-search') as HTMLInputElement)?.addEventListener('input', (e) => {
      this.budgetSearch = (e.target as HTMLInputElement).value;
      const cur = (e.target as HTMLInputElement);
      this.renderBody();
      const next = this.container.querySelector('#bgt-search') as HTMLInputElement;
      if (next) { next.focus(); next.setSelectionRange(cur.value.length, cur.value.length); }
    });
    body.querySelector('#bgt-toggle-all')?.addEventListener('click', () => { this.budgetShowAll = !this.budgetShowAll; this.renderBody(); });

    body.querySelector('#bgt-save-policy')?.addEventListener('click', async () => {
      if (!this.budgets) { this.toast("Budgets didn't load — reload the page, then redo your change"); return; }
      const mode = (body.querySelector('#bgt-mode') as HTMLSelectElement).value;
      const def = Number((body.querySelector('#bgt-default') as HTMLInputElement).value) || 0;
      try {
        const r = await fetch(`${API}/supplies/budgets`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mode, default_monthly: def }) });
        if (!r.ok) { const je = await r.json().catch(() => null); throw new Error((je && je.error) || 'save failed'); }
        this.budgets = await r.json();
        await this.fetchOverview();
        this.renderBody();
        this.toast('Budget policy saved');
      } catch (e: any) { this.toast(e.message || 'Save failed'); }
    });

    body.querySelector('#bgt-save-props')?.addEventListener('click', async () => {
      if (!this.budgets) { this.toast("Budgets didn't load — reload the page, then redo your change"); return; }
      // R114 fix: send ONLY the rows edited here (a patch merged onto a fresh
      // server read) — never the whole override map rebuilt from this page.
      const patch: Record<string, number | null> = {};
      body.querySelectorAll<HTMLInputElement>('.bgt-in').forEach(inp => {
        const id = inp.dataset.listing!;
        const v = inp.value.trim();
        const was = cfg.by_listing[id];
        const before = was == null || (was as any) === '' ? '' : String(was);
        if (v === before) return;
        if (v === '') patch[id] = null;
        else if (Number(v) !== Number(before) || before === '') patch[id] = Number(v) || 0;
      });
      if (!Object.keys(patch).length) { this.toast('No budget changes to save'); return; }
      try {
        const r = await fetch(`${API}/supplies/budgets`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ by_listing_patch: patch }) });
        if (!r.ok) { const je = await r.json().catch(() => null); throw new Error((je && je.error) || 'save failed'); }
        this.budgets = await r.json();
        await this.fetchOverview();
        this.renderBody();
        this.toast('Per-property budgets saved');
      } catch (e: any) { this.toast(e.message || 'Save failed'); }
    });

    this.bindSmartCard(body);
  }

  // ── Smart budgets (R10 #2) ──────────────────────────────────────────────
  private smartCardHtml(cfg: BudgetCfg): string {
    const m = this.smartModel;
    const month = this.smartMonth || this.localMonth();
    const p = this.smartPreview;
    const FLAG_LABEL: Record<string, string> = {
      'no-airroi': 'no AirROI — median occ used',
      'few-stays': '<3 stays — portfolio LOS',
      'no-beds': 'bedrooms unknown (2 used)',
      'no-guests': 'guests unknown (4 used)',
    };
    const previewHtml = p ? `
      <div style="margin-top:14px;">
        <div style="font-size:11.5px;color:var(--text-muted);margin-bottom:8px;">
          ${p.month} · ${p.days_in_month} days · AirROI ${Formatters.escapeHtml(p.basis.airroi_year)} occupancy for ${p.basis.listings_with_airroi} of ${p.rows.length} properties ·
          LOS from ${p.basis.stays_sampled} of your own stays (portfolio avg ${p.basis.portfolio_avg_los} nights) ·
          total ${money(p.total_budget)}/mo${p.applied ? ' · <strong style="color:#16a34a;">applied ✓</strong>' : ' · preview only — nothing saved yet'}
        </div>
        <div style="max-height:420px;overflow:auto;border:1px solid var(--border);border-radius:var(--radius-sm);">
        <table class="sup-tbl">
          <thead><tr><th>Property</th><th style="text-align:right;">Bd</th><th style="text-align:right;">Gst</th><th style="text-align:right;">Occ</th><th style="text-align:right;">LOS</th><th style="text-align:right;">Est res</th><th style="text-align:right;">$ / turn</th><th style="text-align:right;">Budget</th><th>Notes</th></tr></thead>
          <tbody>
            ${p.rows.map(r => `<tr>
              <td>${Formatters.escapeHtml(r.name)}</td>
              <td class="num" style="text-align:right;">${r.bedrooms}</td>
              <td class="num" style="text-align:right;">${r.max_guests}</td>
              <td class="num" style="text-align:right;">${r.occ}%</td>
              <td class="num" style="text-align:right;">${r.los}</td>
              <td class="num" style="text-align:right;">${r.est_reservations}</td>
              <td class="num" style="text-align:right;">${money(r.per_turn)}</td>
              <td class="num" style="text-align:right;font-weight:700;">${money(r.budget)}</td>
              <td style="font-size:10.5px;color:var(--text-faint);">${r.flags.map(f => Formatters.escapeHtml(FLAG_LABEL[f] || f)).join(' · ')}</td>
            </tr>`).join('')}
            <tr style="font-weight:700;"><td>All ${p.rows.length} properties</td><td></td><td></td><td></td><td></td><td class="num" style="text-align:right;">${Math.round(p.rows.reduce((s, r) => s + r.est_reservations, 0))}</td><td></td><td class="num" style="text-align:right;">${money(p.total_budget)}</td><td></td></tr>
          </tbody>
        </table>
        </div>
      </div>` : '';

    return `
      <div class="sup-card">
        <div class="sup-card-head">
          <span class="sup-card-title">Smart budgets ${cfg.smart ? `<span class="pill pill-gray">last applied for ${Formatters.escapeHtml(cfg.smart.month)} · ${cfg.smart.count} properties</span>` : ''}</span>
        </div>
        <div class="sup-cap">
          Auto-size every property's monthly budget from expected turn volume.
          <strong>Est. reservations</strong> = AirROI occupancy × days in the month × seasonal weight ÷ average length of stay
          (from your own bookings, trailing 12 months — AirROI publishes no LOS).
          <strong>Budget</strong> = est. reservations × (base $ + per-bedroom $ × bedrooms + per-guest $ × max guests), floored at the minimum.
          Apply overwrites the per-property budgets below — rerun it each month (or when coefficients change).
        </div>
        <div style="padding:16px 18px;">
          <div class="bgt-config">
            <div><label>Month</label><br><input id="sb-month" type="month" value="${Formatters.escapeHtml(month)}"></div>
            <div><label>Base $ / turn</label><br><input id="sb-base" type="number" min="0" step="0.5" value="${m.base_per_turn}" style="width:80px;"></div>
            <div><label>+ $ / bedroom</label><br><input id="sb-bed" type="number" min="0" step="0.5" value="${m.per_bedroom}" style="width:80px;"></div>
            <div><label>+ $ / guest</label><br><input id="sb-guest" type="number" min="0" step="0.5" value="${m.per_guest}" style="width:80px;"></div>
            <div><label>Minimum $ / mo</label><br><input id="sb-min" type="number" min="0" step="5" value="${m.min_monthly}" style="width:80px;"></div>
            <button class="btn btn-secondary btn-sm" id="sb-preview" style="align-self:flex-end;" ${this.smartBusy ? 'disabled' : ''}>${this.smartBusy ? 'Computing…' : 'Preview'}</button>
            <button class="btn btn-primary btn-sm" id="sb-apply" style="align-self:flex-end;" ${this.smartBusy ? 'disabled' : ''}>Recompute &amp; apply</button>
          </div>
          ${previewHtml}
        </div>
      </div>`;
  }

  private bindSmartCard(body: HTMLElement): void {
    const num = (id: string, fallback: number) => {
      const v = Number((body.querySelector(id) as HTMLInputElement)?.value);
      return Number.isFinite(v) && v >= 0 ? v : fallback;
    };
    const syncInputs = () => {
      // Safari/Firefox render type="month" as plain text — only accept YYYY-MM
      // so free-typed text never round-trips into render state.
      const mv = (body.querySelector('#sb-month') as HTMLInputElement)?.value || '';
      if (/^\d{4}-\d{2}$/.test(mv)) this.smartMonth = mv;
      this.smartModel = {
        base_per_turn: num('#sb-base', this.smartModel.base_per_turn),
        per_bedroom: num('#sb-bed', this.smartModel.per_bedroom),
        per_guest: num('#sb-guest', this.smartModel.per_guest),
        min_monthly: num('#sb-min', this.smartModel.min_monthly),
      };
    };
    body.querySelector('#sb-preview')?.addEventListener('click', () => { syncInputs(); this.smartCall(false); });
    body.querySelector('#sb-apply')?.addEventListener('click', () => {
      syncInputs();
      const n = this.listings.length || 'all';
      if (!confirm(`Recompute and overwrite the monthly budgets for ${n} active properties?\n\nManual per-property values will be replaced by the computed ones (you can still edit any row afterwards).`)) return;
      this.smartCall(true);
    });
  }

  private async smartCall(apply: boolean): Promise<void> {
    if (this.smartBusy) return;
    this.smartBusy = true;
    this.renderBody();
    try {
      const r = await fetch(`${API}/supplies/budgets/smart`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month: this.smartMonth || this.localMonth(), model: this.smartModel, apply }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'smart budget failed');
      this.smartPreview = data;
      this.smartModel = { ...this.smartModel, ...data.model };
      this.smartModelSynced = true;
      if (apply) {
        await Promise.all([this.fetchBudgets(), this.fetchOverview()]);
        this.toast(`Applied — ${data.rows.length} budgets set for ${data.month}`);
      }
    } catch (e: any) { this.toast(e.message || 'Smart budget failed'); }
    this.smartBusy = false;
    this.renderBody();
  }

  // ════════════════════════════════════════════════════════════════════════
  // TAB 1 — STANDARD ITEMS
  // ════════════════════════════════════════════════════════════════════════
  private renderStandard(body: HTMLElement): void {
    const rows = this.standard.map(s => this.standardRowHtml(s)).join('');
    // R115: POST /supplies/prices/check is admin + director (the server decides; an unknown role keeps the button)
    const role = (() => { try { return sessionStorage.getItem('userRole') || ''; } catch { return ''; } })();
    const canCheck = !role || role === 'admin' || role === 'director';
    body.innerHTML = `
      <div class="sup-card">
        <div class="sup-card-head">
          <span class="sup-card-title">Standard Consumables · ${this.standard.length} items</span>
          <div class="sup-filter">
            <span class="sup-price-meta">${this.priceMetaHtml()}</span>
            <button class="btn btn-secondary btn-sm" id="sup-price-check" ${this.priceBusy || !canCheck ? 'disabled' : ''} ${canCheck ? '' : 'title="Admin / director only — the daily check runs on its own"'}>${this.priceBusy ? 'Checking…' : '↺ Check prices now'}</button>
          </div>
        </div>
        <div class="sup-cap">
          <strong>Par basis</strong> sets how each item's par scales per property:
          <strong>per guest</strong> × max guests, <strong>per bedroom</strong> × bedrooms,
          <strong>per bathroom</strong> × bathrooms, or <strong>fixed</strong> (the par qty as-is).
          Par qty counts UNITS; <strong>Pack</strong> is units per pack as sold (12 rolls/pack) — orders and target $ are per pack.
          <strong>Link</strong>: paste an amazon.com product link (or a bare ASIN) to power the 🛒 cart button, other-merchant links open from 🔗.
          <strong>Latest $</strong> is checked once a day from the link for <strong>tracked</strong> items only (☑ Track — on by default when the item has a par; ${this.priceCap} links per run at most; the run stops at the first bot wall) — Amazon usually reads; Costco &amp; other bot-walled stores may fail (shown as why, never a stale number).
        </div>
        <div class="sup-std-scroll"><table class="sup-tbl">
          <colgroup>
            <col style="width:16%"><col style="width:9%"><col style="width:7%">
            <col style="width:11%"><col style="width:6%"><col style="width:6%"><col style="width:8%">
            <col style="width:12%"><col style="width:9%"><col style="width:10%"><col style="width:6%">
          </colgroup>
          <thead><tr>
            <th>Name</th><th>Category</th><th>Unit</th><th>Par basis</th>
            <th>Par qty</th><th>Pack</th><th>Target $ / pack</th><th>Latest $ · trend</th><th>Vendor</th><th>Link</th><th></th>
          </tr></thead>
          <tbody id="sup-std-body">${rows || `<tr><td colspan="11" class="sup-empty">No standard items yet.</td></tr>`}</tbody>
        </table></div>
        <div style="padding:12px 16px;border-top:1px solid var(--border);">
          <button class="sup-link-add" id="sup-add-std">+ Add item</button>
        </div>
      </div>
    `;

    this.bindStandardRows(body);
    body.querySelector('#sup-price-check')?.addEventListener('click', async () => {
      if (this.priceBusy) return;
      // Toggle the button IN PLACE — a full re-render here would wipe unsaved
      // row edits and any not-yet-saved "+ Add item" row.
      const btn = body.querySelector('#sup-price-check') as HTMLButtonElement;
      this.priceBusy = true;
      if (btn) { btn.disabled = true; btn.textContent = 'Checking…'; }
      try {
        const prevRun = this.lastPriceRun;
        const r = await fetch(`${API}/supplies/prices/check`, { method: 'POST' });
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || 'price check failed');
        // Server runs it in the background — poll until last_run advances.
        for (let i = 0; i < 40; i++) {
          await new Promise(done => setTimeout(done, 3000));
          await this.fetchPrices();
          if (this.lastPriceRun && this.lastPriceRun !== prevRun) break;
        }
        const res = this.lastPriceResult;
        this.toast(this.lastPriceRun === prevRun ? 'Price check still running — results will appear on next visit'
          : res && res.stopped ? `Price check stopped early (retailer bot check) — ${res.ok || 0} of ${res.attempted || 0} read`
          : `Price check complete${res ? ` — ${res.ok || 0} of ${res.attempted || 0} read` : ''}`);
      } catch (e: any) { this.toast(e.message || 'Price check failed'); }
      this.priceBusy = false;
      // Only re-render when it can't destroy work in progress.
      const unsavedRow = body.querySelector('tr[data-new]');
      const editing = document.activeElement && body.contains(document.activeElement) && (document.activeElement as HTMLElement).tagName === 'INPUT';
      if (!unsavedRow && !editing) this.renderBody();
      else {
        if (btn) { btn.disabled = false; btn.textContent = '↺ Check prices now'; }
        this.toast('Prices updated — save your edits, then revisit the tab to see them');
      }
    });
    body.querySelector('#sup-add-std')?.addEventListener('click', () => {
      const tbody = body.querySelector('#sup-std-body') as HTMLElement;
      // clear empty-state placeholder
      if (this.standard.length === 0) tbody.innerHTML = '';
      const tmp: StandardItem = { id: '', name: '', category: 'General', unit: 'each', par_basis: 'fixed', par_qty: 1, pack_qty: 1, target_price: null, vendor: '', link: '' };
      const wrap = document.createElement('tbody');
      wrap.innerHTML = this.standardRowHtml(tmp, true);
      const tr = wrap.firstElementChild as HTMLElement;
      tbody.appendChild(tr);
      this.bindStandardRow(tr);
      (tr.querySelector('[data-f="name"]') as HTMLInputElement)?.focus();
    });
  }

  // R115: "prices checked 2h ago · 8 tracked" + why the last run stopped early.
  private priceMetaHtml(): string {
    const tracked = this.standard.filter(priceTracked).length;
    const parts: string[] = [this.lastPriceRun ? `prices checked ${relTime(this.lastPriceRun)}` : 'prices never checked', `${tracked} tracked`];
    const r = this.lastPriceResult;
    if (r && Number(r.deferred) > 0) parts.push(`${r.deferred} waiting for the next run`);
    const stop = r && r.stopped ? `<span class="sup-price-stop" title="${Formatters.escapeHtml(`${r.stopped}${r.stopped_item ? ` (at ${r.stopped_item})` : ''}`)}">last run stopped early — retailer bot check</span>` : '';
    return Formatters.escapeHtml(parts.join(' · ')) + (stop ? ` · ${stop}` : '');
  }

  private standardRowHtml(s: StandardItem, isNew = false): string {
    const opt = (b: ParBasis) => `<option value="${b}" ${s.par_basis === b ? 'selected' : ''}>${BASIS_LABEL[b]}</option>`;
    const p = this.prices.get(s.id);
    const trendBit = (() => {
      if (!p || p.latest == null) return `<span style="color:var(--text-faint);font-size:11px;" title="No successful price check yet — needs a working product link">—</span>`;
      const t = p.trend;
      const up = t && t.change_30d_pct != null && t.change_30d_pct > 3;
      const down = t && t.change_30d_pct != null && t.change_30d_pct < -3;
      const overTarget = p.vs_target_pct != null && p.vs_target_pct > 10;
      return `<span class="num" style="font-weight:600;${overTarget ? 'color:#dc2626;' : ''}" title="checked ${p.checked_at ? relTime(p.checked_at) : '—'}${p.vs_target_pct != null ? ` · ${p.vs_target_pct > 0 ? '+' : ''}${p.vs_target_pct}% vs target` : ''}${t && t.samples ? ` · ${t.samples} samples` : ''}">${money(p.latest)}</span>` +
        (t && t.change_30d_pct != null
          ? ` <span style="font-size:10.5px;font-weight:700;color:${up ? '#dc2626' : down ? '#16a34a' : 'var(--text-faint)'};" title="change over ~30d of checks">${t.change_30d_pct > 0 ? '▲' : t.change_30d_pct < 0 ? '▼' : '·'}${Math.abs(t.change_30d_pct)}%</span>`
          : '');
    })();
    return `
      <tr data-id="${Formatters.escapeHtml(s.id)}" ${isNew ? 'data-new="1"' : ''}>
        <td><input data-f="name" value="${Formatters.escapeHtml(s.name)}" placeholder="Item name"></td>
        <td><input data-f="category" value="${Formatters.escapeHtml(s.category)}"></td>
        <td><input data-f="unit" value="${Formatters.escapeHtml(s.unit)}"></td>
        <td><select data-f="par_basis">${BASES.map(opt).join('')}</select></td>
        <td><input data-f="par_qty" class="num" type="number" min="0" step="1" value="${s.par_qty}"></td>
        <td><input data-f="pack_qty" class="num" type="number" min="1" step="1" value="${s.pack_qty ?? 1}" title="Units per pack as sold — order qty counts packs"></td>
        <td><input data-f="target_price" class="num" type="number" min="0" step="0.01" value="${s.target_price ?? ''}" title="Target $ per PACK"></td>
        <td style="white-space:nowrap;">${trendBit}${p && p.last_error && priceTracked(s) ? ` <span class="sup-price-err" title="last check ${relTime(p.last_error.at)}: ${Formatters.escapeHtml(p.last_error.error)}">!</span>` : ''}
          <label class="sup-track" title="Check this item's price in the daily run (default: on when the item has a par — the box follows the par until you tick or untick it)"><input type="checkbox" data-f="track_price" data-explicit="${typeof s.track_price === 'boolean' ? '1' : ''}" ${priceTracked(s) ? 'checked' : ''}> Track</label></td>
        <td><input data-f="vendor" value="${Formatters.escapeHtml(s.vendor)}"></td>
        <td><input data-f="link" value="${Formatters.escapeHtml(s.link)}" placeholder="https://"></td>
        <td style="white-space:nowrap;">
          <button class="sup-icon-btn" data-act="save" title="Save row" style="color:var(--text);">💾</button>
          <button class="sup-icon-btn" data-act="del" title="Delete">🗑</button>
        </td>
      </tr>`;
  }

  private bindStandardRows(body: HTMLElement): void {
    body.querySelectorAll('#sup-std-body tr').forEach(tr => this.bindStandardRow(tr as HTMLElement));
  }

  private bindStandardRow(tr: HTMLElement): void {
    // R115 round 2: the Track box. An explicit flag is stored ONLY when the
    // user toggles the box (its change event) — never inferred from the par
    // being saved (that turned a par edit 0 → 6 into an opt-out nobody chose,
    // and a durable added at par 0 into a daily-tracked item). Until then a
    // row with no stored flag shows the default for the par as typed, so the
    // box always says what the save will do.
    const tk = tr.querySelector('[data-f="track_price"]') as HTMLInputElement | null;
    const parIn = tr.querySelector('[data-f="par_qty"]') as HTMLInputElement | null;
    tk?.addEventListener('change', () => { tk.dataset.touched = '1'; });
    parIn?.addEventListener('input', () => {
      if (tk && tk.dataset.explicit !== '1' && tk.dataset.touched !== '1') tk.checked = (Number(parIn.value) || 0) > 0;
    });
    tr.querySelector('[data-act="save"]')?.addEventListener('click', async () => {
      const id = tr.dataset.id || '';
      const get = (f: string) => (tr.querySelector(`[data-f="${f}"]`) as HTMLInputElement | HTMLSelectElement)?.value ?? '';
      const name = get('name').trim();
      if (!name) { this.toast('Name is required'); return; }
      const payload: any = {
        id: id || undefined,
        name,
        category: get('category').trim() || 'General',
        unit: get('unit').trim() || 'each',
        par_basis: get('par_basis'),
        par_qty: Number(get('par_qty')) || 0,
        pack_qty: Math.max(1, Math.round(Number(get('pack_qty')) || 1)),
        target_price: get('target_price') === '' ? null : Number(get('target_price')),
        vendor: get('vendor').trim(),
        link: get('link').trim(),
      };
      // R115: send the Track flag only when the user toggled the box. Left
      // out, the server keeps a stored flag, and an unset one stays unset
      // ("tracked when it has a par").
      if (tk && tk.dataset.touched === '1') payload.track_price = tk.checked;
      try {
        const r = await fetch(`${API}/supplies/standard`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || 'save failed');
        this.standard = data.supplies || this.standard;
        this.renderBody();
        this.toast('Saved');
      } catch (e: any) { this.toast(e.message || 'Save failed'); }
    });

    tr.querySelector('[data-act="del"]')?.addEventListener('click', async () => {
      const id = tr.dataset.id || '';
      if (!id) { tr.remove(); return; } // unsaved new row
      try {
        const r = await fetch(`${API}/supplies/standard/${encodeURIComponent(id)}`, { method: 'DELETE' });
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || 'delete failed');
        this.standard = data.supplies || this.standard.filter(s => s.id !== id);
        this.renderBody();
        this.toast('Deleted');
      } catch (e: any) { this.toast(e.message || 'Delete failed'); }
    });
  }

  // ════════════════════════════════════════════════════════════════════════
  // TAB 2 — ORDERS
  // ════════════════════════════════════════════════════════════════════════
  private filteredOrders(): SupplyOrder[] {
    return this.orders.filter(o =>
      (this.orderStatusFilter === 'all' || o.status === this.orderStatusFilter) &&
      (this.orderListingFilter === 'all' || String(o.listing_id) === this.orderListingFilter)
    ).slice().sort((a, b) => (a.requested_at < b.requested_at ? 1 : -1));
  }

  // Spend by property over delivered/verified orders — states its window/filter.
  private spendByProperty(): { name: string; total: number; count: number }[] {
    const map = new Map<string, { name: string; total: number; count: number }>();
    for (const o of this.orders) {
      if (o.status !== 'delivered' && o.status !== 'verified') continue;
      const key = String(o.listing_id);
      const cur = map.get(key) || { name: o.listing_name || this.listingName(key), total: 0, count: 0 };
      cur.total += spendOf(o);   // receipt-backed actual where logged, else estimate
      cur.count += 1;
      map.set(key, cur);
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }

  private renderOrders(body: HTMLElement): void {
    const rows = this.filteredOrders();
    const spend = this.spendByProperty();
    const totalSpend = spend.reduce((s, r) => s + r.total, 0);
    const requestedCount = this.orders.filter(o => o.status === 'requested').length;
    const openCount = this.orders.filter(o => o.status !== 'verified').length;

    body.innerHTML = `
      <div class="kpi-grid cols-3" style="margin-bottom:16px;">
        <div class="kpi-card ${requestedCount > 0 ? 'tone-bad' : ''}">
          <div class="kpi-card-top"><span class="kpi-card-label">Requested (not yet ordered)</span><span class="kpi-card-ic">${icon('receipt', 17)}</span></div>
          <div class="kpi-card-value">${requestedCount}</div>
          <div class="kpi-card-cap">awaiting purchase</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">Open orders</span><span class="kpi-card-ic">${icon('package', 17)}</span></div>
          <div class="kpi-card-value">${openCount}</div>
          <div class="kpi-card-cap">not yet verified · of ${this.orders.length} total</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">Spend logged</span><span class="kpi-card-ic">${icon('wallet', 17)}</span></div>
          <div class="kpi-card-value">${money(totalSpend)}</div>
          <div class="kpi-card-cap">delivered + verified · actual receipts where logged, else estimates</div>
        </div>
      </div>

      <div class="sup-card">
        <div class="sup-card-head">
          <span class="sup-card-title">Restock Orders · ${rows.length} shown</span>
          <div class="sup-filter sup-ord-tools">
            <select id="sup-ord-status">
              <option value="all" ${this.orderStatusFilter === 'all' ? 'selected' : ''}>All statuses</option>
              ${ORDER_FLOW.map(s => `<option value="${s}" ${this.orderStatusFilter === s ? 'selected' : ''}>${s}</option>`).join('')}
            </select>
            <select id="sup-ord-listing">
              <option value="all" ${this.orderListingFilter === 'all' ? 'selected' : ''}>All properties</option>
              <option value="${GLOBAL_LISTING_ID}" ${this.orderListingFilter === GLOBAL_LISTING_ID ? 'selected' : ''}>${GLOBAL_LISTING_NAME}</option>
              ${this.listings.map(l => `<option value="${l.id}" ${this.orderListingFilter === l.id ? 'selected' : ''}>${Formatters.escapeHtml(l.name)}</option>`).join('')}
            </select>
            <button class="btn btn-primary btn-sm" id="sup-new-order">+ New order</button>
          </div>
        </div>
        <div id="sup-order-list">
          ${rows.length ? rows.map(o => this.orderRowHtml(o)).join('') : `<div class="sup-empty">No orders match this filter. Auto-tickets created from the Par / Restock tab will appear here.</div>`}
        </div>
      </div>

      <div class="sup-card">
        <div class="sup-card-head"><span class="sup-card-title">Spend by Property · delivered + verified orders</span></div>
        ${spend.length ? `
        <table class="sup-tbl">
          <colgroup><col style="width:50%"><col style="width:25%"><col style="width:25%"></colgroup>
          <thead><tr><th>Property</th><th style="text-align:right;">Orders</th><th style="text-align:right;">Total spend</th></tr></thead>
          <tbody>
            ${spend.map(r => `<tr><td>${Formatters.escapeHtml(r.name)}</td><td class="num" style="text-align:right;">${r.count}</td><td class="num" style="text-align:right;">${money(r.total)}</td></tr>`).join('')}
            <tr style="font-weight:700;"><td>All properties</td><td class="num" style="text-align:right;">${spend.reduce((s, r) => s + r.count, 0)}</td><td class="num" style="text-align:right;">${money(totalSpend)}</td></tr>
          </tbody>
        </table>` : `<div class="sup-empty">No delivered or verified orders yet — spend appears once an order reaches "delivered".</div>`}
      </div>
    `;

    (body.querySelector('#sup-ord-status') as HTMLSelectElement)?.addEventListener('change', (e) => {
      this.orderStatusFilter = (e.target as HTMLSelectElement).value as any;
      this.renderBody();
    });
    (body.querySelector('#sup-ord-listing') as HTMLSelectElement)?.addEventListener('change', (e) => {
      this.orderListingFilter = (e.target as HTMLSelectElement).value;
      this.renderBody();
    });
    body.querySelector('#sup-new-order')?.addEventListener('click', () => this.openNewOrderModal());
    this.bindOrderRows(body);
  }

  private orderRowHtml(o: SupplyOrder): string {
    const next = ORDER_FLOW[ORDER_FLOW.indexOf(o.status) + 1] || null;
    // R25 G: property-specific variants show their spec inline so the purchaser
    // buys the RIGHT sheets (⭐ navy linen), not the standard ones.
    const itemsStr = o.items.map(it => {
      const variant = it.spec && it.spec.source === 'property' && it.spec.note
        ? ` <span style="font-size:10.5px;font-weight:700;color:#7c3aed;background:#f3e8ff;border-radius:999px;padding:1px 7px;white-space:nowrap;" title="Property-specific variant — not the standard item">⭐ ${Formatters.escapeHtml(it.spec.note)}</span>`
        : '';
      return `${Formatters.escapeHtml(it.name)}${variant} ×${it.qty} pack${it.qty === 1 ? '' : 's'}${it.unit_price ? ` @ ${money(it.unit_price)}/pack` : ''}`;
    }).join(' · ') || '—';
    const pillTitle = next ? `Click to advance → ${next}` : 'Verified — final';
    const cartN = this.cartMatches(o).length;
    const cartTitle = cartN
      ? `Opens amazon.com with ${cartN} of ${o.items.length} item${o.items.length === 1 ? '' : 's'} in the cart (matched to catalog Amazon links)`
      : 'No Amazon links on these items yet — add product links (or ASINs) in the Link column on Standard Items';
    return `
      <div class="sup-order" data-id="${o.id}">
        <div class="sup-order-top">
          <span class="sup-order-name">🏠 ${Formatters.escapeHtml(o.listing_name || this.listingName(o.listing_id))}</span>
          <span class="pill ${o.source !== 'manual' ? 'pill-purple' : 'pill-gray'}">${o.source !== 'manual' ? 'auto' : 'manual'}</span>
          ${(o as any).needed_by ? (() => {
            const nb = String((o as any).needed_by);
            const overdue = o.status === 'requested' && nb <= new Date().toLocaleDateString('en-CA');
            return `<span class="sup-chip" style="${overdue ? 'background:#fee2e2;color:#b91c1c;font-weight:700;' : 'background:#fef3c7;color:#92400e;'}" title="${(o as any).needed_by_source === 'auto' ? 'Auto-matched to the next scheduled clean at this property' : 'Date the requester asked for'}">📅 by ${Formatters.escapeHtml(nb)}${(o as any).needed_by_source === 'auto' ? ' · next clean' : ''}${overdue ? ' ⚠' : ''}</span>`;
          })() : ''}
          ${o.fulfillment ? `<span class="sup-chip" style="${o.fulfillment === 'ground' ? 'background:#cffafe;color:#0e7490;' : 'background:#dbeafe;color:#1d4ed8;'}">${o.fulfillment}</span>` : ''}
          <span style="margin-left:auto;display:flex;gap:8px;align-items:center;">
            ${o.actual_total != null
              ? `<span class="num" style="font-weight:700;color:var(--text);" title="Actual from receipts (estimate was ${money(o.total)})">${money(o.actual_total)} <span style="font-size:9.5px;font-weight:700;color:#16a34a;">ACTUAL</span></span>`
              : `<span class="num" style="font-weight:700;color:var(--text);" title="Estimate — upload receipts to log the actual cost">${money(o.total)} <span style="font-size:9.5px;font-weight:600;color:var(--text-faint);">est</span></span>`}
            <span class="sup-pill ${o.status} ${next ? '' : 'locked'}" data-act="advance" title="${pillTitle}">${o.status}${next ? ' →' : ''}</span>
            <select data-ord-setstatus title="Set any status directly — including back a step (pill only advances)" style="font-size:11px;padding:2px 4px;border:1px solid var(--border);border-radius:6px;color:var(--text-muted);background:transparent;cursor:pointer;">
              ${ORDER_FLOW.map(s => `<option value="${s}" ${o.status === s ? 'selected' : ''}>${s}</option>`).join('')}
            </select>
          </span>
        </div>
        <div class="sup-order-items">${itemsStr}</div>
        ${o.note ? `<div class="sup-order-items" style="font-style:italic;">${Formatters.escapeHtml(o.note)}</div>` : ''}
        ${(o.log && o.log.length) ? `
        <div class="sup-order-items" style="font-size:11.5px;">
          <span data-act="log-toggle" style="cursor:pointer;user-select:none;color:var(--text-faint);" title="Every status change and note, with its timestamp">${this.orderLogOpen.has(o.id) ? '▾' : '▸'} history (${o.log.length})</span>
          ${this.orderLogOpen.has(o.id) ? `
          <div style="margin-top:4px;display:flex;flex-direction:column;gap:2px;">
            ${o.log.slice().reverse().map(l => `<div style="color:var(--text-muted);"><span style="color:var(--text-faint);">${relTime(l.at)}</span> · <strong>${Formatters.escapeHtml(l.status)}</strong>${l.note ? ` — ${Formatters.escapeHtml(l.note)}` : ''}</div>`).join('')}
          </div>` : ''}
        </div>` : ''}
        ${(o.receipts && o.receipts.length) ? `
        <div class="sup-order-items" style="display:flex;gap:10px;flex-wrap:wrap;align-items:center;">
          ${o.receipts.map(r => `
            <span style="display:inline-flex;gap:5px;align-items:center;border:1px solid var(--border);border-radius:8px;padding:4px 7px;">
              <img class="sup-thumb" src="${Formatters.escapeHtml(r.url)}" data-act="rcpt-view" data-rcpt="${Formatters.escapeHtml(r.id)}" title="Receipt · click to view" style="cursor:pointer;">
              <input type="number" min="0" step="0.01" value="${r.amount ?? ''}" placeholder="$" data-role="rcpt-amount" data-rcpt="${Formatters.escapeHtml(r.id)}" title="Receipt total — press Enter or click away to save" style="width:76px;border:1px solid var(--border);border-radius:6px;padding:4px 6px;font-size:12px;text-align:right;font-family:inherit;">
              <button class="sup-icon-btn" data-act="rcpt-del" data-rcpt="${Formatters.escapeHtml(r.id)}" title="Remove receipt">✕</button>
            </span>`).join('')}
          <span style="font-size:11px;color:var(--text-faint);">actual = sum of receipt amounts</span>
        </div>` : ''}
        <div class="sup-order-foot">
          <span>Requested ${relTime(o.requested_at)}${o.requested_by ? ` by ${Formatters.escapeHtml(o.requested_by)}` : ''}${o.assignee ? ` · for ${Formatters.escapeHtml(o.assignee)}` : ''}</span>
          ${o.verify_photo ? `<img class="sup-thumb" src="${Formatters.escapeHtml(o.verify_photo)}" data-act="lightbox" title="Verify photo">` : ''}
          <button class="btn btn-secondary btn-sm" data-act="amzcart" title="${Formatters.escapeHtml(cartTitle)}" ${cartN ? '' : 'style="opacity:.55;"'}>🛒 Amazon cart${cartN ? ` (${cartN}/${o.items.length})` : ''}</button>
          <button class="btn btn-secondary btn-sm" data-act="links" title="Every item's product link (any merchant) in one list">🔗 Links</button>
          <button class="btn btn-secondary btn-sm" data-act="note" title="${o.note ? 'Edit the order note' : 'Add a note to this order'}">📝 ${o.note ? 'Edit note' : 'Note'}</button>
          <button class="btn btn-secondary btn-sm" data-act="receipt">🧾 ${(o.receipts && o.receipts.length) ? 'Add receipt' : 'Receipts'}</button>
          <button class="btn btn-secondary btn-sm" data-act="verify">${o.verify_photo ? '🔄 Replace verify photo' : '📷 Verify photo'}</button>
          <button class="btn btn-secondary btn-sm" data-act="delete" title="Delete this order — removes it from spend &amp; budget math" style="color:var(--danger);">🗑</button>
          <input type="file" accept="image/*" data-role="verify-input" style="display:none;">
          <input type="file" accept="image/*" data-role="receipt-input" style="display:none;">
        </div>
      </div>`;
  }

  // 🔗 Links modal — every item's merchant link (Amazon or not) in one list.
  private openLinksModal(o: SupplyOrder): void {
    const rows = o.items.map(it => {
      const std = this.standard.find(s => s.name.trim().toLowerCase() === it.name.trim().toLowerCase());
      // R25 G: the order line's resolved spec (property variant) wins over the catalog.
      const link = (it.spec && it.spec.link ? it.spec.link : (std && std.link ? std.link : '')).trim();
      const isUrl = /^https?:\/\//i.test(link);
      const asin = SuppliesPage.asinFromLink(link);
      const href = isUrl ? link : (asin ? `https://www.amazon.com/dp/${asin}` : '');
      const variant = it.spec && it.spec.source === 'property' ? (it.spec.note || 'property-specific') : '';
      return { name: it.name, qty: it.qty, vendor: (it.spec && it.spec.vendor) || (std ? std.vendor : ''), href, variant };
    });
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.4);z-index:2000;display:flex;align-items:center;justify-content:center;padding:16px;';
    overlay.innerHTML = `
      <div style="background:#fff;border-radius:var(--radius);width:100%;max-width:520px;max-height:85vh;overflow:auto;">
        <div style="padding:14px 18px;border-bottom:1px solid var(--border);display:flex;justify-content:space-between;align-items:center;">
          <strong style="font-size:15px;">Item links · ${Formatters.escapeHtml(o.listing_name || this.listingName(o.listing_id))}</strong>
          <button data-close style="background:none;border:none;font-size:18px;cursor:pointer;color:var(--text-faint);">✕</button>
        </div>
        <div style="padding:14px 18px;">
          <p style="font-size:12px;color:var(--text-muted);margin:0 0 10px;">Amazon items go in one click via 🛒. Everything else (Costco etc.) opens its product page here — links come from the catalog's Link column.</p>
          ${rows.map(r => `
            <div style="display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid var(--bg-subtle);">
              <span style="flex:1;font-size:13px;">${Formatters.escapeHtml(r.name)}${r.variant ? ` <span style="font-size:10.5px;font-weight:700;color:#7c3aed;background:#f3e8ff;border-radius:999px;padding:1px 7px;">⭐ ${Formatters.escapeHtml(r.variant)}</span>` : ''} ×${r.qty}${r.vendor ? ` <span style="color:var(--text-faint);font-size:11px;">· ${Formatters.escapeHtml(r.vendor)}</span>` : ''}</span>
              ${r.href
                ? `<a href="${Formatters.escapeHtml(r.href)}" target="_blank" rel="noopener" class="btn btn-secondary btn-sm">Open ↗</a>`
                : `<span style="font-size:11px;color:var(--text-faint);">no link in catalog</span>`}
            </div>`).join('')}
        </div>
      </div>`;
    document.body.appendChild(overlay);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
    overlay.querySelector('[data-close]')?.addEventListener('click', () => overlay.remove());
  }

  private bindOrderRows(body: HTMLElement): void {
    body.querySelectorAll('.sup-order').forEach(el => {
      const id = (el as HTMLElement).dataset.id!;
      const o = this.orders.find(x => x.id === id);
      if (!o) return;

      el.querySelector('[data-act="advance"]')?.addEventListener('click', async () => {
        const next = ORDER_FLOW[ORDER_FLOW.indexOf(o.status) + 1];
        if (!next) return; // verified is terminal
        try {
          const r = await fetch(`${API}/supplies/orders/${id}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: next }) });
          const data = await r.json();
          if (!r.ok) throw new Error(data.error || 'update failed');
          Object.assign(o, data.order);
          this.renderBody();
          this.toast(`Status → ${next}`);
        } catch (e: any) { this.toast(e.message || 'Update failed'); }
      });

      // R50: status both ways — the pill only advances; this select also moves
      // BACKWARD (mis-click recovery) with a confirm. Server takes any status.
      const setSel = el.querySelector('[data-ord-setstatus]') as HTMLSelectElement | null;
      setSel?.addEventListener('change', async () => {
        const val = setSel.value as SupplyOrder['status'];
        if (val === o.status) return;
        if (ORDER_FLOW.indexOf(val) < ORDER_FLOW.indexOf(o.status) && !confirm(`Move this order back to "${val}"?`)) {
          setSel.value = o.status;
          return;
        }
        try {
          const r = await fetch(`${API}/supplies/orders/${encodeURIComponent(id)}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: val }) });
          const data = await r.json();
          if (!r.ok) throw new Error(data.error || 'update failed');
          Object.assign(o, data.order);
          this.renderBody();
          this.toast(`Status → ${val}`);
        } catch (e: any) { setSel.value = o.status; this.toast(e.message || 'Update failed'); }
      });

      // R50: order note — prompt prefilled with the current note; empty string clears it.
      el.querySelector('[data-act="note"]')?.addEventListener('click', async () => {
        const raw = prompt('Order note (shows on the card and in history):', o.note || '');
        if (raw == null) return; // cancelled
        const note = raw.trim();   // R50 review: whitespace-only must clear, not persist as a truthy "note"
        try {
          const r = await fetch(`${API}/supplies/orders/${encodeURIComponent(id)}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note }) });
          const data = await r.json();
          if (!r.ok) throw new Error(data.error || 'save failed');
          Object.assign(o, data.order);
          this.renderBody();
          this.toast(note.trim() ? 'Note saved' : 'Note cleared');
        } catch (e: any) { this.toast(e.message || 'Save failed'); }
      });

      // R50: history toggle — expanded ids live on the class so renders keep them open.
      el.querySelector('[data-act="log-toggle"]')?.addEventListener('click', () => {
        if (this.orderLogOpen.has(id)) this.orderLogOpen.delete(id); else this.orderLogOpen.add(id);
        this.renderBody();
      });

      const fileInput = el.querySelector('[data-role="verify-input"]') as HTMLInputElement;
      el.querySelector('[data-act="verify"]')?.addEventListener('click', () => fileInput?.click());
      fileInput?.addEventListener('change', async () => {
        const f = fileInput.files?.[0];
        if (!f) return;
        this.toast('Uploading verify photo…');
        try {
          const fd = new FormData();
          fd.append('photo', f);
          const r = await fetch(`${API}/supplies/orders/${id}/photo`, { method: 'POST', body: fd });
          const data = await r.json();
          if (!r.ok) throw new Error(data.error || 'upload failed');
          Object.assign(o, data.order);
          this.renderBody();
          this.toast('Verified with photo');
        } catch (e: any) { this.toast(e.message || 'Upload failed'); }
      });

      el.querySelector('[data-act="amzcart"]')?.addEventListener('click', () => {
        const matches = this.cartMatches(o);
        if (!matches.length) {
          this.toast('No Amazon links on these items — add product links or ASINs on the Standard Items tab.');
          return;
        }
        const params = matches.map((m, i) => `ASIN.${i + 1}=${m.asin}&Quantity.${i + 1}=${m.qty}`).join('&');
        openExternal(`https://www.amazon.com/gp/aws/cart/add.html?AssociateTag=${AMAZON_CART_TAG}&${params}`, '_blank', 'noopener');
        this.toast(matches.length < o.items.length
          ? `Amazon will ask you to confirm ${matches.length} of ${o.items.length} items — the rest have no Amazon link in the catalog.`
          : 'Amazon will show a confirm page — click "Add To Cart" there.');
      });

      el.querySelector('[data-act="links"]')?.addEventListener('click', () => this.openLinksModal(o));

      el.querySelector('[data-act="delete"]')?.addEventListener('click', async () => {
        if (!confirm(`Delete this order for ${o.listing_name || this.listingName(o.listing_id)}?\n\nIt will be removed from spend and budget math for its month. This can't be undone.`)) return;
        try {
          const r = await fetch(`${API}/supplies/orders/${encodeURIComponent(id)}`, { method: 'DELETE' });
          const data = await r.json();
          if (!r.ok) throw new Error(data.error || 'delete failed');
          this.orders = this.orders.filter(x => x.id !== id);
          await this.fetchOverview();   // spend cockpit reflects the removal
          this.renderBody();
          this.toast('Order deleted');
        } catch (e: any) { this.toast(e.message || 'Delete failed'); }
      });

      // receipts (R11 #4)
      const rcptInput = el.querySelector('[data-role="receipt-input"]') as HTMLInputElement;
      el.querySelector('[data-act="receipt"]')?.addEventListener('click', () => rcptInput?.click());
      rcptInput?.addEventListener('change', async () => {
        const f = rcptInput.files?.[0];
        if (!f) return;
        this.toast('Uploading receipt…');
        try {
          const fd = new FormData();
          fd.append('photo', f);
          const r = await fetch(`${API}/supplies/orders/${encodeURIComponent(id)}/receipts`, { method: 'POST', body: fd });
          const data = await r.json();
          if (!r.ok) throw new Error(data.error || 'upload failed');
          Object.assign(o, data.order);
          this.renderBody();
          this.toast('Receipt added — enter its total to log actual cost');
        } catch (e: any) { this.toast(e.message || 'Upload failed'); }
      });
      el.querySelectorAll<HTMLInputElement>('[data-role="rcpt-amount"]').forEach(inp => {
        const save = async () => {
          const rid = inp.dataset.rcpt!;
          const val = inp.value.trim();
          try {
            const r = await fetch(`${API}/supplies/orders/${encodeURIComponent(id)}/receipts/${encodeURIComponent(rid)}`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ amount: val === '' ? null : Number(val) }),
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data.error || 'save failed');
            Object.assign(o, data.order);
            await this.fetchOverview();
            this.renderBody();
            this.toast(o.actual_total != null ? `Actual cost: ${money(o.actual_total)}` : 'Receipt amount cleared');
          } catch (e: any) { this.toast(e.message || 'Save failed'); }
        };
        inp.addEventListener('change', save);
        inp.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') inp.blur(); });
      });
      el.querySelectorAll('[data-act="rcpt-del"]').forEach(btn => btn.addEventListener('click', async () => {
        const rid = (btn as HTMLElement).dataset.rcpt!;
        if (!confirm('Remove this receipt? Its amount leaves the actual-cost total.')) return;
        try {
          const r = await fetch(`${API}/supplies/orders/${encodeURIComponent(id)}/receipts/${encodeURIComponent(rid)}`, { method: 'DELETE' });
          const data = await r.json();
          if (!r.ok) throw new Error(data.error || 'delete failed');
          Object.assign(o, data.order);
          await this.fetchOverview();
          this.renderBody();
          this.toast('Receipt removed');
        } catch (e: any) { this.toast(e.message || 'Delete failed'); }
      }));
      el.querySelectorAll('[data-act="rcpt-view"]').forEach(img => img.addEventListener('click', () => {
        const rid = (img as HTMLElement).dataset.rcpt!;
        const rc = (o.receipts || []).find(x => x.id === rid);
        if (!rc) return;
        const lb = document.createElement('div');
        lb.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.85);z-index:4000;display:flex;align-items:center;justify-content:center;padding:24px;cursor:pointer;';
        lb.innerHTML = `<img src="${Formatters.escapeHtml(rc.url)}" style="max-width:100%;max-height:90vh;border-radius:10px;">`;
        lb.addEventListener('click', () => lb.remove());
        document.body.appendChild(lb);
      }));

      el.querySelector('[data-act="lightbox"]')?.addEventListener('click', () => {
        if (!o.verify_photo) return;
        const lb = document.createElement('div');
        lb.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.85);z-index:4000;display:flex;align-items:center;justify-content:center;padding:24px;cursor:pointer;';
        lb.innerHTML = `<img src="${Formatters.escapeHtml(o.verify_photo)}" style="max-width:100%;max-height:90vh;border-radius:10px;">`;
        lb.addEventListener('click', () => lb.remove());
        document.body.appendChild(lb);
      });
    });
  }

  // ── new order modal ─────────────────────────────────────────────────────
  // R115 C3 #4 + C4 (Jesse's phone: the "+" add button cut off at the right
  // edge, and "no options to choose from"). The sheet is now a flex column —
  // scrolling body, sticky footer (running total + Create order) — that is a
  // bottom sheet on phones; the item row is a grid with no fixed widths; and
  // the standard supplies are LISTED (lib/orderCatalog.mjs): the priced
  // catalog for the chosen property (its ⭐ variants applied), then the
  // inventory checklist's items that have no catalog price yet. The item box
  // searches that list (its catalog <datalist> autocomplete is kept); a name
  // that matches nothing is added as a custom line. A failed catalog read says
  // so (never an empty list), and free text always works — nothing here can
  // block an order.
  private openNewOrderModal(): void {
    this.draftItems = [];
    const esc = (v: any) => Formatters.escapeHtml(v == null ? '' : String(v));
    const office = (() => { try { return ['admin', 'director', 'manager', 'remote_ops'].includes(sessionStorage.getItem('userRole') || ''); } catch { return false; } })();
    const overlay = document.createElement('div');
    overlay.className = 'sup-no-wrap m-sheet-wrap';
    overlay.innerHTML = `
      <div class="sup-no-card m-sheet" role="dialog" aria-modal="true" aria-labelledby="sup-no-title">
        <div class="sup-no-head">
          <strong id="sup-no-title">New restock order</strong>
          <button id="sup-no-close" class="sup-no-x" type="button" aria-label="Close" title="Close">✕</button>
        </div>
        <div class="sup-no-body">
          <label class="sup-no-lab" for="sup-no-listing">Property</label>
          <select id="sup-no-listing" class="sup-no-in" style="margin-bottom:16px;">
            <option value="">— Select property —</option>
            <option value="${GLOBAL_LISTING_ID}">${GLOBAL_LISTING_NAME} (deck detergent, shared tools…)</option>
            ${this.listings.map(l => `<option value="${esc(l.id)}">${esc(l.name)}</option>`).join('')}
          </select>

          <div class="sup-no-two">
            <div>
              <label class="sup-no-lab" for="sup-no-fulfillment">Fulfillment</label>
              <select id="sup-no-fulfillment" class="sup-no-in">
                <option value="remote">Remote — ordered online for delivery</option>
                <option value="ground">Ground — someone buys it locally</option>
              </select>
            </div>
            <div>
              <label class="sup-no-lab" for="sup-no-assignee">Assignee (optional)</label>
              <input id="sup-no-assignee" class="sup-no-in" placeholder="who fulfills this">
            </div>
          </div>

          <label class="sup-no-lab" for="sup-no-item">Add items — tap ＋ on a supply below, or search / type one here · qty counts PACKS, $ is per pack</label>
          <div class="sup-no-row">
            <input id="sup-no-item" class="sup-no-in" list="sup-std-list" placeholder="Search supplies or type a new item" autocomplete="off" enterkeyhint="done">
            <input id="sup-no-qty" class="sup-no-in" type="number" inputmode="numeric" min="1" step="1" value="1" placeholder="Qty" aria-label="Packs">
            <input id="sup-no-price" class="sup-no-in" type="number" inputmode="decimal" min="0" step="0.01" placeholder="$ / pack" aria-label="$ per pack">
            <button id="sup-no-add" class="btn btn-secondary" type="button">+ Add</button>
          </div>
          <datalist id="sup-std-list">
            ${this.standard.map(s => `<option value="${esc(s.name)}" data-price="${s.target_price ?? ''}">`).join('')}
          </datalist>

          <div id="sup-no-items" style="margin-bottom:4px;"></div>
          <div id="sup-no-catalog" class="sup-cat"></div>

          <label class="sup-no-lab" for="sup-no-note">Note (optional)</label>
          <textarea id="sup-no-note" class="sup-no-in" rows="2" style="font-size:13px;"></textarea>
        </div>
        <div class="sup-no-foot" data-bottom-bar>
          <div id="sup-no-err" style="color:var(--danger);font-size:13px;margin-bottom:8px;display:none;"></div>
          <div class="sup-no-foot-row">
            <span class="sup-no-sum" id="sup-no-sum"></span>
            <button id="sup-no-submit" class="btn btn-primary" type="button">Create order (requested)</button>
          </div>
        </div>
      </div>`;
    document.body.appendChild(overlay);

    const listingSel = overlay.querySelector('#sup-no-listing') as HTMLSelectElement;
    const itemEl = overlay.querySelector('#sup-no-item') as HTMLInputElement;
    const qtyEl = overlay.querySelector('#sup-no-qty') as HTMLInputElement;
    const priceEl = overlay.querySelector('#sup-no-price') as HTMLInputElement;
    const bodyEl = overlay.querySelector('.sup-no-body') as HTMLElement;
    const catEl = overlay.querySelector('#sup-no-catalog') as HTMLElement;

    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') tryClose(); };
    const close = () => { document.removeEventListener('keydown', onKey); overlay.remove(); };
    // A stray tap on the backdrop (the top strip of a phone sheet) must not
    // throw away a half-built order; ✕ is deliberate and closes outright.
    const tryClose = () => { if (this.draftItems.length && !window.confirm('Discard this order? The items you added are not saved.')) return; close(); };
    document.addEventListener('keydown', onKey);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) tryClose(); });
    overlay.querySelector('#sup-no-close')?.addEventListener('click', close);

    // ── the catalog the picker lists ──
    // Before a property is picked (or for Global) the standard catalog; then
    // GET /supplies/property/:lid `effective` (the property's ⭐ variant
    // note / price applied; a request counter drops stale answers). A failed
    // property read falls back to the standard catalog and says so.
    let propEff: any[] | null = null;
    let propErr = false;
    let propLoading = false;
    let propReq = 0;
    const groupOpen = new Map<string, boolean>();   // categories the user opened / closed
    let itemByKey = new Map<string, CatalogItem>();
    const catalogRows = (): any[] => propEff ?? this.standard;
    const catalogFailed = () => propEff == null && this.standardErr;
    const catalogMatch = (name: string): any | null => {
      const n = name.trim().toLowerCase();
      return n ? (catalogRows().find(s => String(s.name || '').trim().toLowerCase() === n) || null) : null;
    };

    const subLine = (it: CatalogItem) => [
      it.pack_qty > 1 ? `pack of ${it.pack_qty}${it.unit ? ` ${it.unit}` : ''}` : (it.unit || ''),
      it.price != null ? `${money(it.price)}/pack` : (it.source === 'inventory' ? 'no catalog price yet' : 'no price set'),
      it.vendor,
    ].filter(Boolean).join(' · ');
    const rowHtml = (it: CatalogItem) => {
      const q = qtyInDraft(this.draftItems, it.name);
      return `<div class="sup-cat-row${q ? ' in' : ''}" data-cat-row="${esc(it.key)}">
        <div class="sup-cat-main"><div class="sup-cat-name">${esc(it.name)}${it.variant ? ` <span class="sup-cat-var" title="This property uses a specific variant">⭐ ${esc(it.note)}</span>` : ''}</div><div class="sup-cat-sub">${esc(subLine(it))}</div></div>
        <div class="sup-qty"><button type="button" class="sup-qbtn" data-cat-minus="${esc(it.key)}" aria-label="One pack fewer of ${esc(it.name)}" ${q ? '' : 'disabled'}>−</button><span class="sup-qval">${q}</span><button type="button" class="sup-qbtn" data-cat-plus="${esc(it.key)}" aria-label="One more pack of ${esc(it.name)}">+</button></div>
      </div>`;
    };

    const renderCatalog = () => {
      const q = itemEl.value.trim();
      const built = buildOrderCatalog({ effective: catalogFailed() ? [] : catalogRows(), inventoryCategories: this.invCats || [] });
      itemByKey = new Map(built.groups.flatMap(g => g.items.map(i => [i.key, i] as [string, CatalogItem])));
      const groups: CatalogGroup[] = filterOrderCatalog(built.groups, q);
      const catGroups = groups.filter(g => g.source === 'catalog');
      const invGroups = groups.filter(g => g.source === 'inventory');
      const shown = groups.reduce((n, g) => n + g.items.length, 0);
      const exact = !!q && built.groups.some(g => g.items.some(i => i.name.toLowerCase() === q.toLowerCase()));
      const parts: string[] = [];
      parts.push(`<div class="sup-cat-h"><b>Standard supplies</b><span>${q
        ? `${shown} match${shown === 1 ? '' : 'es'} for “${esc(q)}”`
        : `${built.counts.catalog} priced${this.invCats ? ` · ${built.counts.inventory} from the inventory checklist` : ''}`}</span></div>`);
      if (catalogFailed()) {
        parts.push(`<div class="sup-cat-msg err" role="alert">⚠ The supply catalog didn't load — reload the page to see it. You can still type items above.</div>`);
      } else {
        if (propLoading) parts.push(`<div class="sup-cat-msg">Loading this property's variants…</div>`);
        if (propErr) parts.push(`<div class="sup-cat-msg">This property's variants didn't load — standard catalog prices are shown.</div>`);
        if (!built.counts.catalog && !q) parts.push(`<div class="sup-cat-msg">The catalog has no items yet — add them on Standard Items.</div>`);
        if (catGroups.length) {
          parts.push(`<div class="sup-cat-sec">Catalog · priced</div>`);
          for (const g of catGroups) parts.push(`${catGroups.length > 1 ? `<div class="sup-cat-lbl">${esc(g.label)}</div>` : ''}${g.items.map(rowHtml).join('')}`);
        }
      }
      if (this.invLoad) parts.push(`<div class="sup-cat-msg">Loading the inventory checklist…</div>`);
      else if (this.invErr) parts.push(`<div class="sup-cat-msg">The inventory checklist didn't load — only catalog items are listed.</div>`);
      else if (invGroups.length) {
        parts.push(`<div class="sup-cat-sec inv">Inventory checklist · no catalog price yet</div>`);
        for (const g of invGroups) {
          const open = q ? true : (groupOpen.has(g.key) ? groupOpen.get(g.key)! : !g.collapsed);
          parts.push(`<details class="sup-cat-group" data-cat-group="${esc(g.key)}" ${open ? 'open' : ''}><summary>${esc(g.label)}<span class="n">${g.items.length}</span></summary>${g.items.map(rowHtml).join('')}</details>`);
        }
      }
      if (q && !shown && !catalogFailed()) parts.push(`<div class="sup-cat-msg">Nothing in the catalog or the inventory checklist matches “${esc(q)}”.</div>`);
      if (q && !exact) parts.push(`<button type="button" class="btn btn-ghost sup-cat-custom" data-cat-custom>＋ Not listed? Add “${esc(q)}” as a custom item</button>`);
      catEl.innerHTML = parts.join('');
    };

    // Steppers update in place (no list redraw), so a tap never jumps the list.
    const syncRows = () => {
      catEl.querySelectorAll<HTMLElement>('[data-cat-row]').forEach(row => {
        const it = itemByKey.get(row.dataset.catRow || '');
        if (!it) return;
        const q = qtyInDraft(this.draftItems, it.name);
        row.classList.toggle('in', q > 0);
        const v = row.querySelector('.sup-qval'); if (v) v.textContent = String(q);
        const m = row.querySelector<HTMLButtonElement>('[data-cat-minus]'); if (m) m.disabled = q <= 0;
      });
    };

    const isCatalogLine = (l: DraftLine) => l.source === 'catalog' || !!catalogMatch(l.name) || catalogRows().some(s => baseName(s.name) === baseName(l.name));
    const renderDraft = () => {
      const wrap = overlay.querySelector('#sup-no-items') as HTMLElement;
      const sum = overlay.querySelector('#sup-no-sum') as HTMLElement;
      const lines = this.draftItems;
      const total = draftTotal(lines);
      const unpriced = lines.filter(l => l.unpriced || !Number(l.unit_price)).length;
      sum.innerHTML = lines.length
        ? `${lines.length} item${lines.length === 1 ? '' : 's'} · <b>${money(total)}</b>${unpriced ? ` · ${unpriced} unpriced` : ''}`
        : 'No items yet';
      if (!lines.length) { wrap.innerHTML = `<div class="sup-draft-none">No items added yet.</div>`; return; }
      wrap.innerHTML = `
        <table class="sup-tbl sup-draft" style="border:1px solid var(--border);border-radius:var(--radius-sm);">
          <colgroup><col style="width:50%"><col style="width:16%"><col style="width:20%"><col style="width:14%"></colgroup>
          <thead><tr><th>Item</th><th style="text-align:right;">Packs</th><th style="text-align:right;">$ / pack</th><th></th></tr></thead>
          <tbody>
            ${lines.map((it, i) => `<tr>
              <td>${esc(it.name)}${office && !catalogFailed() && !isCatalogLine(it) ? `<br><button type="button" class="sup-save-cat" data-save-cat="${i}" title="Add this item to the standard catalog (Standard Items) so it lists here next time">＋ Save to catalog</button>` : ''}</td>
              <td class="num" style="text-align:right;">${it.qty}</td>
              <td class="num" style="text-align:right;">${it.unpriced || !Number(it.unit_price) ? '<span class="sup-noprice">no price</span>' : money(it.unit_price)}</td>
              <td style="text-align:right;"><button type="button" class="sup-icon-btn" data-rm="${i}" aria-label="Remove ${esc(it.name)}">🗑</button></td>
            </tr>`).join('')}
            <tr style="font-weight:700;"><td>Total</td><td></td><td class="num" style="text-align:right;">${money(total)}</td><td></td></tr>
          </tbody>
        </table>`;
      wrap.querySelectorAll<HTMLElement>('[data-rm]').forEach(b => b.addEventListener('click', () => {
        this.draftItems.splice(Number(b.dataset.rm), 1);
        renderDraft(); syncRows();
      }));
      wrap.querySelectorAll<HTMLButtonElement>('[data-save-cat]').forEach(b => b.addEventListener('click', () => void saveToCatalog(Number(b.dataset.saveCat), b)));
    };

    // Re-anchor the scroll on the row the user tapped: the draft table above
    // the list grows / shrinks, which would otherwise shift that row away.
    const anchored = (anchor: Element | null, fn: () => void) => {
      const before = anchor ? anchor.getBoundingClientRect().top : 0;
      fn();
      if (anchor && anchor.isConnected) { const d = anchor.getBoundingClientRect().top - before; if (Math.abs(d) > 0.5) bodyEl.scrollTop += d; }
    };
    const bump = (it: CatalogItem, delta: number, row: Element | null) => anchored(row, () => {
      this.draftItems = addToDraft(this.draftItems, { name: it.name, price: it.price, source: it.source }, delta);
      renderDraft(); syncRows();
    });

    // "+ Add" / Enter / the custom-item button: the typed name — a catalog or
    // checklist name adopts that row (exact name + price), anything else is a
    // custom line (unpriced unless a $ was typed).
    const addTyped = () => {
      const name = itemEl.value.trim();
      const qty = Math.round(Number(qtyEl.value)) || 0;
      if (!name || qty <= 0) { this.toast('Enter an item name and qty'); return; }
      const hit = [...itemByKey.values()].find(i => i.name.toLowerCase() === name.toLowerCase()) || null;
      const typed = priceEl.value === '' ? null : Number(priceEl.value);
      const item = hit ? { name: hit.name, price: typed ?? hit.price, source: hit.source } : { name, price: typed, source: 'custom' as const };
      this.draftItems = addToDraft(this.draftItems, item, qty);
      itemEl.value = ''; priceEl.value = ''; qtyEl.value = '1';
      overlay.querySelector('#sup-no-packhint')?.remove();
      renderDraft(); renderCatalog();
      itemEl.focus();
    };

    // R115 C4 (office): a custom / checklist line → the standard catalog
    // (existing POST /supplies/standard, if_absent = idempotent by name), so
    // it lists — and prices — next time. Serialized client-side.
    let saveChain: Promise<void> = Promise.resolve();
    const saveToCatalog = (i: number, btn: HTMLButtonElement) => {
      const line = this.draftItems[i];
      if (!line) return Promise.resolve();
      btn.disabled = true; btn.textContent = 'Saving…';
      const inv = itemByKey.get(`inventory:${line.name}`);
      saveChain = saveChain.then(async () => {
        try {
          const r = await fetch(`${API}/supplies/standard`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: line.name, category: inv ? inv.category : 'General', target_price: line.unpriced || !Number(line.unit_price) ? null : line.unit_price, if_absent: true }),
          });
          const j = await r.json().catch(() => ({} as any));
          if (!r.ok || !j.item) throw new Error(j.error || `HTTP ${r.status}`);
          const item: StandardItem = j.item;
          if (!this.standard.some(s => s.id === item.id)) this.standard.push(item);
          if (propEff && !propEff.some((s: any) => s.id === item.id)) propEff.push({ ...item, note: '', source: 'standard' });
          const at = this.draftItems.findIndex(l => l.name === line.name);
          if (at >= 0) {
            const priced = item.target_price != null && Number.isFinite(Number(item.target_price));
            const cur = this.draftItems[at];
            this.draftItems[at] = { ...cur, name: item.name, source: 'catalog', ...(cur.unpriced && priced ? { unit_price: Number(item.target_price), unpriced: false } : {}) };
          }
          const dl = overlay.querySelector('#sup-std-list');
          if (dl && !j.existed) dl.insertAdjacentHTML('beforeend', `<option value="${esc(item.name)}" data-price="${item.target_price ?? ''}">`);
          if (overlay.isConnected) { renderDraft(); renderCatalog(); }
          this.toast(j.existed ? `${item.name} is already in the catalog` : `${item.name} saved to the catalog — set its pack size and price on Standard Items`);
        } catch (e: any) {
          if (btn.isConnected) { btn.disabled = false; btn.textContent = '＋ Save to catalog'; }
          this.toast(`Couldn't save to the catalog — ${e.message || 'try again'}`);
        }
      });
      return saveChain;
    };

    catEl.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      const step = t.closest<HTMLElement>('[data-cat-plus], [data-cat-minus]');
      if (step) {
        const plus = step.hasAttribute('data-cat-plus');
        const it = itemByKey.get((plus ? step.dataset.catPlus : step.dataset.catMinus) || '');
        if (it) bump(it, plus ? 1 : -1, step.closest('[data-cat-row]'));
        return;
      }
      if (t.closest('[data-cat-custom]')) { addTyped(); return; }
      const sum = t.closest('summary');
      if (sum && !itemEl.value.trim()) { const d = sum.parentElement as HTMLDetailsElement; if (d.dataset.catGroup) groupOpen.set(d.dataset.catGroup, !d.open); }
    });

    // The item box searches the list; an exact catalog name still autofills
    // its $ / pack (the property's variant price) and the pack-size hint.
    itemEl.addEventListener('input', () => {
      const match = catalogMatch(itemEl.value);
      if (match && match.target_price != null && !priceEl.value) priceEl.value = String(match.target_price);
      // Pack-size hint so "8 rolls" doesn't get ordered as 8 PACKS by accident.
      let hint = overlay.querySelector('#sup-no-packhint') as HTMLElement | null;
      if (match && (match.pack_qty ?? 1) > 1) {
        if (!hint) {
          hint = document.createElement('div');
          hint.id = 'sup-no-packhint';
          hint.className = 'sup-no-hint';
          itemEl.parentElement!.insertAdjacentElement('afterend', hint);
        }
        hint.textContent = `${match.name} sells as a pack of ${match.pack_qty} ${match.unit} — qty above counts packs, $ is per pack.`;
      } else if (hint) hint.remove();
      renderCatalog();
    });
    [itemEl, qtyEl, priceEl].forEach(el => el.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); addTyped(); } }));
    overlay.querySelector('#sup-no-add')?.addEventListener('click', addTyped);

    listingSel.addEventListener('change', async () => {
      const lid = listingSel.value;
      const my = ++propReq;
      propEff = null; propErr = false; propLoading = false;
      if (!lid || lid === GLOBAL_LISTING_ID) { renderCatalog(); return; }
      propLoading = true; renderCatalog();
      try {
        const r = await fetch(`${API}/supplies/property/${encodeURIComponent(lid)}`);
        const j = await r.json().catch(() => null);
        if (my !== propReq) return;
        if (!r.ok || !j || !Array.isArray(j.effective)) throw new Error(`HTTP ${r.status}`);
        propEff = j.effective;
      } catch { if (my !== propReq) return; propErr = true; }
      propLoading = false;
      if (overlay.isConnected) { renderCatalog(); renderDraft(); }
    });

    void this.loadInventoryTemplate().then(() => { if (overlay.isConnected) renderCatalog(); });
    renderDraft();
    renderCatalog();

    const submitOrder = async (override: boolean) => {
      const listingId = listingSel.value;
      const err = overlay.querySelector('#sup-no-err') as HTMLElement;
      if (!listingId) { err.textContent = 'Pick a property.'; err.style.display = 'block'; return; }
      if (!this.draftItems.length) { err.textContent = 'Add at least one item.'; err.style.display = 'block'; return; }
      err.style.display = 'none';
      const items = toOrderItems(this.draftItems);   // exactly { name, qty, unit_price } per line
      const note = (overlay.querySelector('#sup-no-note') as HTMLTextAreaElement).value.trim();
      const fulfillment = (overlay.querySelector('#sup-no-fulfillment') as HTMLSelectElement).value;
      const assignee = (overlay.querySelector('#sup-no-assignee') as HTMLInputElement).value.trim();
      const submit = overlay.querySelector('#sup-no-submit') as HTMLButtonElement;
      submit.disabled = true; submit.textContent = 'Creating…';
      try {
        const r = await fetch(`${API}/supplies/orders`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ listing_id: listingId, listing_name: listingSel.selectedOptions[0]?.text, items, note, source: 'manual', fulfillment, assignee, override }),
        });
        const data = await r.json();
        if (r.status === 409 && data.error === 'over_budget') {
          // hard budget block — show the message and offer an override
          err.innerHTML = `${Formatters.escapeHtml(data.message || 'Over the monthly supply budget.')}<br><button id="sup-no-override" class="btn btn-sm" style="margin-top:8px;background:var(--danger);color:#fff;">Order anyway (over budget)</button>`;
          err.style.display = 'block';
          submit.disabled = false; submit.textContent = 'Create order (requested)';
          overlay.querySelector('#sup-no-override')?.addEventListener('click', () => submitOrder(true));
          return;
        }
        // R70 #8: owner-handled property — office may deliberately step in.
        if (r.status === 400 && data.error === 'owner_handled') {
          err.innerHTML = `${Formatters.escapeHtml(data.message || 'The owner handles supplies at this property.')}<br><button id="sup-no-oh-override" class="btn btn-sm" style="margin-top:8px;background:var(--danger);color:#fff;">Order anyway (owner asked us to)</button>`;
          err.style.display = 'block';
          submit.disabled = false; submit.textContent = 'Create order (requested)';
          overlay.querySelector('#sup-no-oh-override')?.addEventListener('click', async () => {
            submit.disabled = true;
            const r2 = await fetch(`${API}/supplies/orders`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ listing_id: listingId, listing_name: listingSel.selectedOptions[0]?.text, items, note, source: 'manual', fulfillment, assignee, override, owner_handled_override: true }),
            }).catch(() => null);
            if (r2 && r2.ok) { close(); await Promise.all([this.fetchOrders(), this.fetchOverview()]); this.renderBody(); this.toast('Order created (owner-handled override)'); }
            else { submit.disabled = false; err.textContent = 'Override failed — try again.'; }
          });
          return;
        }
        if (!r.ok) throw new Error(data.message || data.error || 'create failed');
        close();
        await Promise.all([this.fetchOrders(), this.fetchOverview()]);
        this.renderBody();
        // soft-mode over-budget warning is informational, not blocking
        if (data.budget && data.budget.would_exceed && data.budget.mode === 'soft') {
          this.toast(`Order created — note: now $${data.budget.projected.toFixed(2)} of a $${data.budget.limit.toFixed(2)} budget`);
        } else {
          this.toast('Order created');
        }
      } catch (e: any) {
        err.textContent = e.message || 'Failed to create order.'; err.style.display = 'block';
        submit.disabled = false; submit.textContent = 'Create order (requested)';
      }
    };
    overlay.querySelector('#sup-no-submit')?.addEventListener('click', () => submitOrder(false));
  }

  // ════════════════════════════════════════════════════════════════════════
  // TAB 3 — PAR / RESTOCK
  // ════════════════════════════════════════════════════════════════════════
  private async loadPar(): Promise<void> {
    if (!this.parListingId) { this.parData = null; this.renderBody(); return; }
    this.parLoading = true;
    this.renderBody();
    try {
      const r = await fetch(`${API}/supplies/par/${encodeURIComponent(this.parListingId)}`);
      this.parData = await r.json();
    } catch { this.parData = null; }
    this.parLoading = false;
    this.renderBody();
  }

  // basis math string, e.g. "2 × 4 guests = 8" — uses the standard item's per-unit
  // par_qty cross-referenced against the listing's basis count.
  private basisMath(row: ParRow): string {
    const std = this.standard.find(s => s.name === row.name);
    const counts = this.parData?.basis_counts;
    if (!std || !counts) return `par ${row.par_qty}`;
    const per = std.par_qty;
    if (row.basis === 'fixed') return `${row.par_qty} (fixed)`;
    const countMap: Record<string, [number | null, string]> = {
      per_guest: [counts.max_guests, 'guests'],
      per_bedroom: [counts.bedrooms, 'bedrooms'],
      per_bathroom: [counts.bathrooms, 'bathrooms'],
    };
    const [cnt, lbl] = countMap[row.basis] || [null, ''];
    if (cnt == null) return `${per} ${BASIS_LABEL[row.basis]} × (no ${lbl} on listing) = ${row.par_qty}`;
    return `${per} × ${cnt} ${lbl} = ${row.par_qty}`;
  }

  // ── R25 G: Property Items — per-property variants over the standard catalog ──
  // "Sheets at ABC are navy linen, everywhere else standard white." Pick a
  // property, then set a variant note / link / vendor / $ on any catalog item.
  // Orders for that property automatically resolve to the variant.
  private renderPropertyItems(body: HTMLElement): void {
    const selector = `
      <div class="sup-card-head">
        <span class="sup-card-title">Property-Specific Items</span>
        <div class="sup-filter">
          <select id="sup-prop-listing">
            <option value="">— Pick a property —</option>
            ${this.listings.map(l => `<option value="${l.id}" ${this.propListingId === l.id ? 'selected' : ''}>${Formatters.escapeHtml(l.name)}</option>`).join('')}
          </select>
        </div>
      </div>`;

    let inner = '';
    if (this.propLoading) {
      inner = `<div class="sup-empty">Loading…</div>`;
    } else if (!this.propListingId) {
      inner = `<div class="sup-empty">Pick a property. Any catalog item can get a property-specific variant — type/color note, its own product link, vendor, or price. Orders for that property automatically use the variant; everything else falls back to the standard item.</div>`;
    } else if (!this.propItems.length) {
      inner = `<div class="sup-empty">No standard items in the catalog yet — add them on Standard Items first.</div>`;
    } else {
      const nCustom = this.propItems.filter(i => i.source === 'property').length;
      inner = `
        <p style="font-size:12px;color:var(--text-muted);margin:0 0 10px;">${nCustom ? `${nCustom} item${nCustom === 1 ? ' has' : 's have'} a property-specific variant here.` : 'No variants yet — everything uses the standard item.'} Fill in a variant note (e.g. "navy linen, queen") and/or link, then Save. Clearing all fields returns the item to standard.</p>
        <div style="overflow-x:auto;"><table class="sup-tbl" style="table-layout:auto;">
          <thead><tr><th>Item</th><th>Using</th><th>Variant (type / color)</th><th>Link</th><th>Vendor</th><th style="width:70px;">$ / pack</th><th></th></tr></thead>
          <tbody>
            ${this.propItems.map(it => {
              // Inputs show ONLY what's actually overridden — merged/standard
              // values stay as placeholders, so Save round-trips exactly what
              // the operator set and catalog updates keep flowing through.
              const ov = this.propOverrides[it.id] || {};
              const std = this.standard.find(s => s.id === it.id);
              return `
              <tr data-prop-row="${it.id}">
                <td><strong>${Formatters.escapeHtml(it.name)}</strong><div style="font-size:11px;color:var(--text-faint);">${Formatters.escapeHtml(it.category)}</div></td>
                <td>${it.source === 'property' ? '<span style="font-size:10.5px;font-weight:700;color:#7c3aed;background:#f3e8ff;border-radius:999px;padding:2px 8px;white-space:nowrap;">⭐ this property</span>' : '<span style="font-size:10.5px;color:var(--text-faint);white-space:nowrap;">standard</span>'}</td>
                <td><input type="text" data-prop-note="${it.id}" value="${Formatters.escapeHtml(ov.note || '')}" placeholder="e.g. navy linen, queen" style="width:160px;"></td>
                <td><input type="text" data-prop-link="${it.id}" value="${Formatters.escapeHtml(ov.link || '')}" placeholder="${std?.link ? 'std: ' + Formatters.escapeHtml(std.link.slice(0, 28)) + '…' : 'product link…'}" style="width:150px;"></td>
                <td><input type="text" data-prop-vendor="${it.id}" value="${Formatters.escapeHtml(ov.vendor || '')}" placeholder="${Formatters.escapeHtml(std?.vendor || '—')}" style="width:90px;"></td>
                <td><input type="number" step="0.01" data-prop-price="${it.id}" value="${ov.target_price != null ? ov.target_price : ''}" placeholder="${std?.target_price != null ? String(std.target_price) : '—'}" style="width:64px;"></td>
                <td><button class="sup-btn" data-prop-save="${it.id}" style="font-size:11px;padding:3px 10px;">Save</button></td>
              </tr>`; }).join('')}
          </tbody>
        </table></div>`;
    }

    body.innerHTML = `<div class="sup-card">${selector}${inner}</div>`;

    body.querySelector('#sup-prop-listing')?.addEventListener('change', e => {
      this.propListingId = (e.target as HTMLSelectElement).value;
      void this.loadPropertyItems();
    });
    body.querySelectorAll<HTMLButtonElement>('[data-prop-save]').forEach(btn => btn.addEventListener('click', async () => {
      const id = btn.dataset.propSave!;
      const val = (sel: string) => (body.querySelector(`[data-${sel}="${id}"]`) as HTMLInputElement | null)?.value.trim() ?? '';
      btn.disabled = true; btn.textContent = '…';
      try {
        const r = await fetch(`${API}/supplies/property/${encodeURIComponent(this.propListingId)}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ item_id: id, note: val('prop-note'), link: val('prop-link'), vendor: val('prop-vendor'), target_price: val('prop-price') || null }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        await this.loadPropertyItems();
        this.toast('Saved');
      } catch (e: any) { this.toast(`Save failed — ${e.message}`); btn.disabled = false; btn.textContent = 'Save'; }
    }));
  }

  private async loadPropertyItems(): Promise<void> {
    if (!this.propListingId) { this.propItems = []; this.propOverrides = {}; this.renderBody(); return; }
    const req = ++this.propReq;
    const lid = this.propListingId;
    this.propLoading = true; this.renderBody();
    try {
      const r = await fetch(`${API}/supplies/property/${encodeURIComponent(lid)}`);
      const j = await r.json();
      if (req !== this.propReq) return;   // user switched properties mid-flight
      this.propItems = j.effective || [];
      this.propOverrides = j.overrides || {};
    } catch { if (req !== this.propReq) return; this.propItems = []; this.propOverrides = {}; }
    this.propLoading = false;
    this.renderBody();
  }

  private renderPar(body: HTMLElement): void {
    const counts = this.parData?.basis_counts;
    const selector = `
      <div class="sup-card-head">
        <span class="sup-card-title">Par / Restock by Property</span>
        <div class="sup-filter">
          <select id="sup-par-listing">
            <option value="">— Pick a property —</option>
            ${this.listings.map(l => `<option value="${l.id}" ${this.parListingId === l.id ? 'selected' : ''}>${Formatters.escapeHtml(l.name)}</option>`).join('')}
          </select>
        </div>
      </div>`;

    let inner = '';
    if (this.parLoading) {
      inner = `<div class="sup-empty">Computing par…</div>`;
    } else if (!this.parListingId) {
      inner = `<div class="sup-empty">Pick a property to see each standard item's computed par (with the basis math), then enter an observed count to auto-ticket the shortfall.</div>`;
    } else if (!this.parData || !this.parData.par) {
      inner = `<div class="sup-empty">Couldn't load par for this property.</div>`;
    } else {
      const cap = counts
        ? `Par computed against this property's capacity: <strong>${counts.max_guests ?? '—'}</strong> guests · <strong>${counts.bedrooms ?? '—'}</strong> bedrooms · <strong>${counts.bathrooms ?? '—'}</strong> bathrooms. Enter what you counted on site; if it's below par a <strong>requested</strong> auto-ticket for the shortfall is created (source: auto-inspection).`
        : '';
      const rows = this.parData.par.map(row => {
        const std = this.standard.find(s => s.name === row.name);
        const price = std?.target_price ?? null;
        return `
          <tr data-name="${Formatters.escapeHtml(row.name)}" data-par="${row.par_qty}">
            <td>${Formatters.escapeHtml(row.name)}</td>
            <td><span class="sup-par-num num">${row.par_qty}</span> <span class="sup-math">${this.basisMath(row)}</span></td>
            <td class="num" style="text-align:right;">${price != null ? money(price) : '—'}</td>
            <td style="text-align:right;"><input class="sup-obs-in num" type="number" min="0" step="1" data-role="obs" placeholder="count"></td>
            <td><span class="sup-shortfall" data-role="short">—</span></td>
            <td style="text-align:right;"><button class="btn btn-secondary btn-sm" data-role="ticket">Auto-ticket</button></td>
          </tr>`;
      }).join('');
      inner = `
        <div class="sup-cap">${cap}</div>
        <table class="sup-tbl">
          <colgroup><col style="width:20%"><col style="width:30%"><col style="width:11%"><col style="width:12%"><col style="width:13%"><col style="width:14%"></colgroup>
          <thead><tr><th>Item</th><th>Par (with basis)</th><th style="text-align:right;">Target $</th><th style="text-align:right;">Observed</th><th>Shortfall</th><th></th></tr></thead>
          <tbody>${rows}</tbody>
        </table>`;
    }

    body.innerHTML = `<div class="sup-card">${selector}${inner}</div>`;

    (body.querySelector('#sup-par-listing') as HTMLSelectElement)?.addEventListener('change', (e) => {
      this.parListingId = (e.target as HTMLSelectElement).value;
      this.loadPar();
    });

    body.querySelectorAll('tr[data-name]').forEach(tr => this.bindParRow(tr as HTMLElement));
  }

  private bindParRow(tr: HTMLElement): void {
    const name = tr.dataset.name!;
    const par = Number(tr.dataset.par) || 0;
    const obsEl = tr.querySelector('[data-role="obs"]') as HTMLInputElement;
    const shortEl = tr.querySelector('[data-role="short"]') as HTMLElement;

    const recompute = () => {
      if (obsEl.value === '') { shortEl.textContent = '—'; shortEl.className = 'sup-shortfall'; return; }
      const obs = Number(obsEl.value) || 0;
      const shortfall = par - obs;
      if (shortfall > 0) { shortEl.textContent = `short ${shortfall} (par ${par}, obs ${obs})`; shortEl.className = 'sup-shortfall short'; }
      else { shortEl.textContent = `at par (par ${par}, obs ${obs})`; shortEl.className = 'sup-shortfall ok'; }
    };
    obsEl.addEventListener('input', recompute);

    tr.querySelector('[data-role="ticket"]')?.addEventListener('click', async () => {
      if (obsEl.value === '') { this.toast('Enter an observed count first'); return; }
      const observed = Number(obsEl.value) || 0;
      try {
        const r = await fetch(`${API}/supplies/auto-ticket`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ listing_id: this.parListingId, item: name, observed_count: observed }),
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || 'auto-ticket failed');
        if (data.skipped) {
          this.toast(data.reason && data.reason !== 'observed_count meets or exceeds par' ? `No ticket — ${data.reason}` : `No ticket — observed ${observed} ≥ par ${par}`);
        } else {
          await this.fetchOrders();
          this.toast(`Ticket created: ${data.shortfall} ${name} (par ${data.par}, observed ${data.observed})`);
        }
      } catch (e: any) { this.toast(e.message || 'Auto-ticket failed'); }
    });
  }
}
